import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ReactFlow,
  Background,
  MiniMap,
  Controls,
  BackgroundVariant,
  addEdge,
  useNodesState,
  useEdgesState,
  type Node,
  type Edge,
  type Connection,
  type NodeChange,
  type EdgeChange,
  Handle,
  Position,
  useReactFlow,
  ReactFlowProvider,
  MarkerType,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import {
  MessageSquare,
  ListChecks,
  PenLine,
  GitBranch,
  UserCheck,
  StopCircle,
  Save,
  CheckCircle,
  Circle,
  Trash2,
  Plus,
  LayoutGrid,
  Bot,
  Send,
  RotateCcw,
  ChevronDown,
  ChevronRight,
  X,
  AlertTriangle,
  Info,
  FileText,
  Zap,
  KeyRound,
  Megaphone,
  FileSearch,
  Calendar,
  Phone,
  Search,
  Image,
  Rows3,
  Video,
  File,
} from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';

import { WhatsAppHeader, WhatsAppNav } from '@/components/whatsapp/WhatsAppWorkspace';
import { Card } from '@/components/Card';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { EnhancePromptButton } from '@/components/EnhancePromptButton';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip } from 'recharts';
import { FormField } from '@/components/FormField';
import {
  botFlowsService,
  BotFlow,
  BotState,
  BotTriggerType,
  BotTriggerConfig,
  ChoiceOption,
  ChoiceSection,
  ChoiceMediaType,
  ConditionBranch,
  CollectState,
  EndState,
  SimHistoryEntry,
  FlowAnalytics,
} from '@/services/bot-flows.service';
import { templatesService, Template } from '@/services/templates.service';
import { useActiveAccount } from '@/hooks/useActiveAccount';
import { validateFlowClient } from '@/utils/flow-validator';
import { ConversationTranscript, TranscriptMessage } from '@/components/leads/ConversationTranscript';

// ─── Constants ────────────────────────────────────────────────────────────────

type TabId = 'editor' | 'json' | 'simulator' | 'analytics';

const TRIGGER_LABELS: Record<BotTriggerType, string> = {
  ad: 'Ad',
  lead_ad_form: 'Lead Form',
  keyword: 'Keyword',
  manual: 'Manual',
  scheduled: 'Scheduled',
};

const STATE_TYPES = [
  { type: 'message', icon: <MessageSquare size={15} />, label: 'Send Message', desc: 'Plain text or a WhatsApp template' },
  { type: 'choice', icon: <ListChecks size={15} />, label: 'Buttons / Choices', desc: 'Show buttons; each routes to a different next step' },
  { type: 'choice:media_buttons', icon: <Image size={15} />, label: 'Media + Buttons', desc: 'Image/video/document header + up to 3 buttons' },
  { type: 'choice:list', icon: <Rows3 size={15} />, label: 'List Menu', desc: 'Up to 10 grouped rows opened from a CTA' },
  { type: 'ai', icon: <Bot size={15} />, label: 'AI conversation', desc: 'Follow your instructions and route by customer intent' },
  { type: 'collect', icon: <PenLine size={15} />, label: 'Ask Question', desc: 'Wait for the user’s reply and save it' },
  { type: 'condition', icon: <GitBranch size={15} />, label: 'Branch on Data', desc: 'Route on a saved value (e.g. {{city}} == "Dubai")' },
  { type: 'handoff', icon: <UserCheck size={15} />, label: 'Hand off to Agent', desc: 'Pause the bot and assign to a human' },
  { type: 'end', icon: <StopCircle size={15} />, label: 'End Conversation', desc: 'Mark lead qualified, lost, or spam' },
] as const;

type StateTypeKey = 'message' | 'choice' | 'collect' | 'condition' | 'handoff' | 'end' | 'ai';

const NODE_COLORS: Record<StateTypeKey, { bg: string; border: string; header: string }> = {
  ai: { bg: 'bg-primary-soft', border: 'border-primary', header: 'bg-primary-soft text-primary' },
  message: {
    bg: 'bg-primary-soft',
    border: 'border-primary',
    header: 'bg-primary-soft text-primary',
  },
  choice: {
    bg: 'bg-info-soft',
    border: 'border-info',
    header: 'bg-info-soft text-info',
  },
  collect: {
    bg: 'bg-primary-soft',
    border: 'border-primary-dark',
    header: 'bg-primary-soft text-primary-dark',
  },
  condition: {
    bg: 'bg-warning-soft',
    border: 'border-warning',
    header: 'bg-warning-soft text-warning',
  },
  handoff: {
    bg: 'bg-surface-alt',
    border: 'border-primary-dark',
    header: 'bg-surface-elevated text-primary-dark',
  },
  end: {
    bg: 'bg-danger-soft',
    border: 'border-danger',
    header: 'bg-danger-soft text-danger',
  },
};

const STATE_ICONS: Record<StateTypeKey, React.ReactNode> = {
  ai: <Bot size={13} />,
  message: <MessageSquare size={13} />,
  choice: <ListChecks size={13} />,
  collect: <PenLine size={13} />,
  condition: <GitBranch size={13} />,
  handoff: <UserCheck size={13} />,
  end: <StopCircle size={13} />,
};

// ─── Custom Node ──────────────────────────────────────────────────────────────

interface FlowNodeData {
  state: BotState;
  isStart: boolean;
  hasError: boolean;
  errorMessages: string[];
  hasWarning: boolean;
  warningMessages: string[];
  onContextMenu?: (e: React.MouseEvent, stateId: string) => void;
  templatesByUid?: Map<string, { name: string; body: string; buttons: { text: string }[] }>;
  [key: string]: unknown;
}

function FlowNode({ data, selected }: { data: FlowNodeData; selected?: boolean }) {
  const { state, isStart, hasError, errorMessages, hasWarning, warningMessages, onContextMenu, templatesByUid } = data;
  const type = state.type as StateTypeKey;
  const colors = NODE_COLORS[type] ?? NODE_COLORS.message;
  const icon = STATE_ICONS[type];

  // Resolve linked template (Message state)
  const linkedTemplate = state.type === 'message' && state.templateUid
    ? templatesByUid?.get(state.templateUid)
    : undefined;

  const promptText = state.type === 'ai' ? state.systemPrompt : (state as unknown as Record<string, unknown>).prompt as string | undefined;

  const choicePresentation = state.type === 'choice' ? (state.presentationType ?? 'buttons') : null;
  // `choiceHandles` is the flat row order used to position the bottom source
  // handles. For list mode we flatten sections into the same order that
  // `options[]` is mirrored into (see StatePropPanel), so handle indexes align.
  const choiceHandles = state.type === 'choice' ? (state.options ?? []) : [];
  const conditionHandles =
    state.type === 'condition'
      ? [...(state.branches ?? []).map((_, i) => `branch_${i}`), 'fallback']
      : [];

  const mediaIcon = state.type === 'choice' && choicePresentation === 'media_buttons'
    ? (state.mediaType === 'video' ? <Video size={11} /> : state.mediaType === 'document' ? <File size={11} /> : <Image size={11} />)
    : null;

  return (
    <div
      className={clsx(
        'relative rounded-xl border-2 shadow-soft min-w-[220px] max-w-[260px]',
        'transition-shadow duration-150',
        colors.bg,
        hasError ? 'border-danger ring-2 ring-danger/30' : colors.border,
        selected && !hasError && 'ring-2 ring-primary/50',
      )}
      onContextMenu={(e) => onContextMenu?.(e, state.id)}
    >
      {/* Target handle (top) */}
      <Handle
        type="target"
        position={Position.Top}
        className="!w-3 !h-3 !bg-border-strong !border-2 !border-surface"
      />

      {/* Header */}
      <div className={clsx('flex items-center gap-1.5 rounded-t-xl px-3 py-2', colors.header)}>
        {icon}
        <span className="text-[11px] font-semibold uppercase tracking-wider opacity-80">{type}</span>
        <span className="ml-auto font-mono text-[10px] opacity-60 truncate">{state.id}</span>
        {isStart && (
          <span className="ml-1 rounded-full bg-green-500/20 text-green-600 dark:text-green-400 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wide border border-green-500/30">
            START
          </span>
        )}
      </div>

      {/* Body */}
      <div className="px-3 py-2 space-y-2 min-h-[32px]">
        {/* Linked WhatsApp template preview */}
        {linkedTemplate && (
          <div className="rounded-md border border-border bg-surface/70 px-2 py-1.5">
            <div className="flex items-center gap-1 text-[10px] font-semibold text-primary mb-0.5">
              <FileText size={9} /> {linkedTemplate.name}
            </div>
            <p className="text-[10px] text-text-secondary line-clamp-2 leading-relaxed">
              {linkedTemplate.body}
            </p>
            {linkedTemplate.buttons?.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1">
                {linkedTemplate.buttons.slice(0, 3).map((b, i) => (
                  <span key={i} className="inline-flex items-center rounded-full border border-primary/30 bg-primary-soft/40 text-primary px-1.5 py-0.5 text-[9px] font-medium">
                    {b.text}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Plain prompt text (Message / Choice / Collect) */}
        {!linkedTemplate && promptText && (
          <p className="text-[11px] text-text-secondary line-clamp-3 leading-relaxed whitespace-pre-wrap">
            {promptText}
          </p>
        )}

        {/* Choice — media header preview (media_buttons mode only) */}
        {state.type === 'choice' && choicePresentation === 'media_buttons' && (
          <div className="rounded-md border border-primary/30 bg-primary-soft/30 px-2 py-1.5 flex items-center gap-2">
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary-soft text-primary">
              {mediaIcon}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-primary">
                {state.mediaType ?? 'image'} header
              </div>
              <div className="text-[10px] text-text-muted truncate font-mono">
                {state.mediaUrl || 'no URL set'}
              </div>
            </div>
          </div>
        )}

        {/* Choice — section groups (list mode) */}
        {state.type === 'choice' && choicePresentation === 'list' && (
          <div className="flex flex-col gap-1.5">
            {(state.sections ?? []).map((sec, si) => (
              <div key={si} className="rounded-md border border-info/20 bg-info-soft/15 p-1.5">
                {sec.title && (
                  <div className="mb-1 px-0.5 text-[9px] font-semibold uppercase tracking-wide text-info">
                    {sec.title}
                  </div>
                )}
                <div className="flex flex-col gap-1">
                  {(sec.rows ?? []).map((row, ri) => (
                    <div
                      key={`${si}-${ri}`}
                      className={clsx(
                        'flex items-center justify-between rounded-md border px-2 py-1 text-[10px] font-medium',
                        row.nextStateId
                          ? 'border-info/40 bg-info-soft/60 text-info'
                          : 'border-dashed border-warning/50 bg-warning-soft/30 text-warning',
                      )}
                      title={row.nextStateId ? `→ ${row.nextStateId}` : 'Not wired'}
                    >
                      <span className="truncate">{row.label || `Row ${ri + 1}`}</span>
                      <ChevronRight size={10} className="shrink-0 opacity-70" />
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {(state.sections ?? []).length === 0 && (
              <div className="rounded-md border border-dashed border-warning/50 bg-warning-soft/20 px-2 py-1.5 text-[10px] text-warning">
                List has no sections yet
              </div>
            )}
          </div>
        )}

        {/* Choice — flat buttons (buttons + media_buttons modes) */}
        {state.type === 'choice' && choicePresentation !== 'list' && choiceHandles.length > 0 && (
          <div className="flex flex-col gap-1">
            {choiceHandles.map((opt, i) => (
              <div
                key={`${opt.value}-${i}`}
                className={clsx(
                  'flex items-center justify-between rounded-md border px-2 py-1 text-[10px] font-medium',
                  opt.nextStateId
                    ? 'border-info/40 bg-info-soft/40 text-info'
                    : 'border-dashed border-warning/50 bg-warning-soft/30 text-warning',
                )}
                title={opt.nextStateId ? `→ ${opt.nextStateId}` : 'Not wired — drag from the matching dot below'}
              >
                <span className="truncate">{opt.label || `Option ${i + 1}`}</span>
                <ChevronRight size={10} className="shrink-0 opacity-70" />
              </div>
            ))}
          </div>
        )}

        {/* Collect — show what's being saved */}
        {state.type === 'collect' && (
          <div className="text-[10px] text-text-muted">
            <span className="font-semibold text-text-secondary">Save as:</span>{' '}
            <span className="font-mono">{state.saveAs || '—'}</span>
            {state.expectedType && state.expectedType !== 'text' && (
              <span className="ml-1 opacity-70">({state.expectedType})</span>
            )}
          </div>
        )}

        {/* Condition — list branch expressions */}
        {state.type === 'condition' && (
          <div className="space-y-1">
            {(state.branches ?? []).slice(0, 4).map((b, i) => (
              <div key={i} className="flex items-center justify-between rounded-md border border-warning/40 bg-warning-soft/30 px-2 py-1 text-[10px] text-warning">
                <span className="font-mono truncate">{b.when || 'when …'}</span>
                <ChevronRight size={10} className="shrink-0 opacity-70" />
              </div>
            ))}
            <div className="flex items-center justify-between rounded-md border border-dashed border-text-muted/40 px-2 py-1 text-[10px] text-text-muted">
              <span>else (fallback)</span>
              <ChevronRight size={10} className="shrink-0 opacity-60" />
            </div>
          </div>
        )}

        {/* Handoff — show assignee */}
        {state.type === 'handoff' && (
          <div className="text-[10px] text-text-muted">
            <span className="font-semibold text-text-secondary">Assign to:</span>{' '}
            <span className="font-mono">{state.assignTo}</span>
          </div>
        )}

        {/* End — show terminal status */}
        {state.type === 'end' && (
          <div className={clsx(
            'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
            state.terminalStatus === 'qualified' && 'bg-green-500/15 text-green-600 dark:text-green-400',
            state.terminalStatus === 'lost' && 'bg-danger-soft text-danger',
            state.terminalStatus === 'spam' && 'bg-surface-alt text-text-muted',
          )}>
            {state.terminalStatus}
          </div>
        )}
      </div>

      {/* Error / warning badges */}
      {(hasError || hasWarning) && (
        <div className="px-3 pb-2 flex flex-wrap gap-1">
          {hasError && (
            <span title={errorMessages.join('; ')} className="inline-flex items-center gap-1 rounded-full bg-danger-soft/80 text-danger px-1.5 py-0.5 text-[10px] font-medium cursor-help">
              <AlertTriangle size={9} />
              {errorMessages.length} error{errorMessages.length > 1 ? 's' : ''}
            </span>
          )}
          {!hasError && hasWarning && (
            <span title={warningMessages.join('; ')} className="inline-flex items-center gap-1 rounded-full bg-warning-soft/80 text-warning px-1.5 py-0.5 text-[10px] font-medium cursor-help">
              <Info size={9} />
              {warningMessages.length} warning{warningMessages.length > 1 ? 's' : ''}
            </span>
          )}
        </div>
      )}

      {state.type === 'ai' && <div className="px-3 pb-3 text-[10px] text-text-muted">{state.endIntents.length} outcomes · up to {state.maxTurnsBeforeHandoff ?? 10} turns</div>}
      {state.type === 'ai' && <div className="absolute -bottom-3 left-0 flex w-full justify-around">
        {[...state.endIntents.map(intent => `intent:${intent}`), 'fallback'].map((handle, index, all) => <Handle key={handle} type="source" position={Position.Bottom} id={handle} title={handle.replace('intent:', '')}
          style={{ left: `${(index + 1) * 100 / (all.length + 1)}%` }} className="!h-3 !w-3 !border-2 !border-surface !bg-primary" />)}
      </div>}
      {/* Source handles */}
      {state.type === 'message' && (
        <Handle
          type="source"
          position={Position.Bottom}
          id="next"
          className="!w-3 !h-3 !bg-border-strong !border-2 !border-surface"
        />
      )}
      {state.type === 'collect' && (
        <Handle
          type="source"
          position={Position.Bottom}
          id="next"
          className="!w-3 !h-3 !bg-border-strong !border-2 !border-surface"
        />
      )}
      {state.type === 'choice' && choiceHandles.length > 0 && (
        <div className="absolute -bottom-3 left-0 w-full flex justify-around">
          {choiceHandles.map((opt, i) => (
            <Handle
              key={opt.value}
              type="source"
              position={Position.Bottom}
              id={`option_${i}`}
              style={{ position: 'relative', transform: 'none', left: 'auto', bottom: 'auto' }}
              className="!w-3 !h-3 !bg-info/60 !border-2 !border-surface"
              title={opt.label}
            />
          ))}
        </div>
      )}
      {state.type === 'condition' && conditionHandles.length > 0 && (
        <div className="absolute -bottom-3 left-0 w-full flex justify-around">
          {conditionHandles.map((handle) => (
            <Handle
              key={handle}
              type="source"
              position={Position.Bottom}
              id={handle}
              style={{ position: 'relative', transform: 'none', left: 'auto', bottom: 'auto' }}
              className="!w-3 !h-3 !bg-warning/60 !border-2 !border-surface"
              title={handle}
            />
          ))}
        </div>
      )}
      {state.type === 'handoff' && (
        <Handle
          type="source"
          position={Position.Bottom}
          id="next"
          className="!w-3 !h-3 !bg-primary-dark/60 !border-2 !border-surface"
        />
      )}
      {/* end has no source handle */}
    </div>
  );
}

// ─── Layout ───────────────────────────────────────────────────────────────────

function autoLayout(states: BotState[], startStateId: string): Map<string, { x: number; y: number }> {
  const positions = new Map<string, { x: number; y: number }>();
  if (!states.length) return positions;

  const stateMap = new Map(states.map((s) => [s.id, s]));

  // BFS level assignment
  const levels = new Map<string, number>();
  const queue: string[] = [];

  if (stateMap.has(startStateId)) {
    queue.push(startStateId);
    levels.set(startStateId, 0);
  }

  while (queue.length > 0) {
    const id = queue.shift()!;
    const s = stateMap.get(id);
    if (!s) continue;
    const currentLevel = levels.get(id) ?? 0;

    const neighbors = getStateNeighbors(s);
    for (const n of neighbors) {
      if (n && stateMap.has(n) && !levels.has(n)) {
        levels.set(n, currentLevel + 1);
        queue.push(n);
      }
    }
  }

  // Any unvisited states go to extra level
  let maxLevel = 0;
  levels.forEach((l) => { if (l > maxLevel) maxLevel = l; });
  for (const s of states) {
    if (!levels.has(s.id)) levels.set(s.id, maxLevel + 1);
  }

  // Group by level
  const byLevel = new Map<number, string[]>();
  levels.forEach((level, id) => {
    const arr = byLevel.get(level) ?? [];
    arr.push(id);
    byLevel.set(level, arr);
  });

  const NODE_W = 240;
  const NODE_H = 120;
  const H_GAP = 40;
  const V_GAP = 80;

  byLevel.forEach((ids, level) => {
    const totalWidth = ids.length * NODE_W + (ids.length - 1) * H_GAP;
    const startX = -totalWidth / 2;
    ids.forEach((id, i) => {
      positions.set(id, {
        x: startX + i * (NODE_W + H_GAP),
        y: level * (NODE_H + V_GAP),
      });
    });
  });

  return positions;
}

function getStateNeighbors(state: BotState): string[] {
  switch (state.type) {
    case 'message': return state.next ? [state.next] : [];
    case 'choice': return (state.options ?? []).map((o) => o.nextStateId);
    case 'collect': return [state.next];
    case 'ai': return [...Object.values(state.intentToNextState ?? {}), ...(state.fallbackStateId ? [state.fallbackStateId] : [])];
    case 'condition': {
      const ns = (state.branches ?? []).map((b) => b.nextStateId);
      if (state.fallbackStateId) ns.push(state.fallbackStateId);
      return ns;
    }
    default: return [];
  }
}

// ─── Convert flow states to RF nodes/edges ───────────────────────────────────

function stateToNodes(
  states: BotState[],
  startStateId: string,
  positions: Map<string, { x: number; y: number }>,
  errorsByStateId: Map<string, string[]>,
  warningsByStateId: Map<string, string[]>,
  onContextMenu: (e: React.MouseEvent, stateId: string) => void,
  templatesByUid: Map<string, { name: string; body: string; buttons: { text: string }[] }>,
): Node[] {
  return states.map((state, idx) => {
    const pos = positions.get(state.id) ?? { x: idx * 260, y: 0 };
    const errorMessages = errorsByStateId.get(state.id) ?? [];
    const warningMessages = warningsByStateId.get(state.id) ?? [];
    return {
      id: state.id,
      type: 'botState',
      position: pos,
      data: {
        state,
        isStart: state.id === startStateId,
        hasError: errorMessages.length > 0,
        errorMessages,
        hasWarning: warningMessages.length > 0,
        warningMessages,
        onContextMenu,
        templatesByUid,
      } as FlowNodeData,
    };
  });
}

function statesToEdges(states: BotState[]): Edge[] {
  const edges: Edge[] = [];
  for (const state of states) {
    switch (state.type) {
      case 'message':
        if (state.next) {
          edges.push({
            id: `${state.id}->next`,
            source: state.id,
            sourceHandle: 'next',
            target: state.next,
            markerEnd: { type: MarkerType.ArrowClosed },
            style: { stroke: 'var(--color-border-strong)' },
          });
        }
        break;
      case 'collect':
        if (state.next) {
          edges.push({
            id: `${state.id}->next`,
            source: state.id,
            sourceHandle: 'next',
            target: state.next,
            markerEnd: { type: MarkerType.ArrowClosed },
            style: { stroke: 'var(--color-border-strong)' },
          });
        }
        break;
      case 'choice':
        for (let i = 0; i < (state.options ?? []).length; i++) {
          const opt = state.options[i];
          if (opt.nextStateId) {
            edges.push({
              id: `${state.id}->option_${i}`,
              source: state.id,
              sourceHandle: `option_${i}`,
              target: opt.nextStateId,
              label: opt.label.slice(0, 15),
              markerEnd: { type: MarkerType.ArrowClosed },
              style: { stroke: 'var(--color-info)' },
              labelStyle: { fontSize: 10 },
            });
          }
        }
        break;
      case 'ai':
        for (const [intent, target] of Object.entries(state.intentToNextState ?? {})) {
          if (target) edges.push({ id: `${state.id}->intent:${intent}`, source: state.id, sourceHandle: `intent:${intent}`, target, label: intent,
            markerEnd: { type: MarkerType.ArrowClosed }, style: { stroke: 'var(--color-primary)' }, labelStyle: { fontSize: 10 } });
        }
        if (state.fallbackStateId) edges.push({ id: `${state.id}->fallback`, source: state.id, sourceHandle: 'fallback', target: state.fallbackStateId, label: 'fallback',
          markerEnd: { type: MarkerType.ArrowClosed }, style: { stroke: 'var(--color-primary)', strokeDasharray: '4,4' }, labelStyle: { fontSize: 10 } });
        break;
      case 'condition':
        for (let i = 0; i < (state.branches ?? []).length; i++) {
          const branch = state.branches[i];
          if (branch.nextStateId) {
            edges.push({
              id: `${state.id}->branch_${i}`,
              source: state.id,
              sourceHandle: `branch_${i}`,
              target: branch.nextStateId,
              label: branch.when.slice(0, 15),
              markerEnd: { type: MarkerType.ArrowClosed },
              style: { stroke: 'var(--color-warning)' },
              labelStyle: { fontSize: 10 },
            });
          }
        }
        if (state.fallbackStateId) {
          edges.push({
            id: `${state.id}->fallback`,
            source: state.id,
            sourceHandle: 'fallback',
            target: state.fallbackStateId,
            label: 'else',
            markerEnd: { type: MarkerType.ArrowClosed },
            style: { stroke: 'var(--color-warning)', strokeDasharray: '4,4' },
            labelStyle: { fontSize: 10 },
          });
        }
        break;
    }
  }
  return edges;
}

// ─── Right panel ─────────────────────────────────────────────────────────────

function SelectDropdown({
  value,
  onChange,
  options,
  placeholder = 'Select...',
  className,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
  className?: string;
}) {
  return (
    <div className={clsx('relative', className)}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={clsx(
          'w-full h-9 rounded-lg border border-border bg-surface px-3 pr-8 text-sm text-text-primary',
          'focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary-soft appearance-none',
        )}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      <ChevronDown size={13} className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-text-muted" />
    </div>
  );
}

function Textarea({
  value,
  onChange,
  placeholder,
  rows = 3,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
}) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      rows={rows}
      placeholder={placeholder}
      className={clsx(
        'w-full resize-y rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-muted',
        'focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary-soft',
      )}
    />
  );
}

interface TriggerPanelProps {
  flow: BotFlow;
  onChange: (patch: Partial<BotFlow>) => void;
}

function TriggerPanel({ flow, onChange }: TriggerPanelProps) {
  const tc = flow.triggerConfig;
  const setTc = (patch: Partial<typeof tc>) => onChange({ triggerConfig: { ...tc, ...patch } });
  type ListFieldKey = 'adIds' | 'adSetIds' | 'campaignIds' | 'formIds' | 'keywords';
  const listField = (
    label: string,
    key: ListFieldKey,
  ) => (
    <FormField label={label} hint="One per line">
      <Textarea
        rows={2}
        value={(tc[key] ?? []).join('\n')}
        onChange={(v) =>
          setTc({ [key]: v.split('\n').map((s) => s.trim()).filter(Boolean) })
        }
        placeholder="Enter IDs, one per line..."
      />
    </FormField>
  );

  return (
    <div className="space-y-3 p-4">
      <p className="text-xs font-semibold text-text-muted uppercase tracking-wider">Trigger Config</p>
      <FormField label="Trigger type">
        <SelectDropdown
          value={flow.triggerType}
          onChange={(v) => onChange({ triggerType: v as BotTriggerType })}
          options={Object.entries(TRIGGER_LABELS).map(([k, v]) => ({ value: k, label: v }))}
        />
      </FormField>
      <FormField label="Language" hint="IETF tag — leads with matching preferredLanguage are routed here">
        <SelectDropdown
          value={flow.language ?? 'en'}
          onChange={(v) => onChange({ language: v })}
          options={[
            { value: 'en', label: 'English (en)' },
            { value: 'ur', label: 'Urdu (ur)' },
            { value: 'ar', label: 'Arabic (ar)' },
            { value: 'es', label: 'Spanish (es)' },
            { value: 'fr', label: 'French (fr)' },
            { value: 'de', label: 'German (de)' },
            { value: 'hi', label: 'Hindi (hi)' },
            { value: 'pt', label: 'Portuguese (pt)' },
            { value: 'tr', label: 'Turkish (tr)' },
            { value: 'zh', label: 'Chinese (zh)' },
          ]}
        />
      </FormField>
      <FormField label="Flow group" hint="Paste the same value across translations of the same flow to link them">
        <input
          type="text"
          value={flow.flowGroupUid ?? ''}
          onChange={(e) => onChange({ flowGroupUid: e.target.value || undefined })}
          placeholder="optional — e.g. menu-bot-v1"
          className="w-full rounded-lg border border-border bg-surface px-3 py-1.5 text-sm text-text-primary focus:outline-none focus:border-primary"
        />
      </FormField>
      {flow.triggerType === 'ad' && (
        <>
          {listField('Ad IDs', 'adIds')}
          {listField('Ad Set IDs', 'adSetIds')}
          {listField('Campaign IDs', 'campaignIds')}
        </>
      )}
      {flow.triggerType === 'lead_ad_form' && listField('Form IDs', 'formIds')}
      {flow.triggerType === 'keyword' && (
        <FormField label="Keywords (regex supported)" hint="One per line">
          <Textarea
            rows={3}
            value={(tc.keywords ?? []).join('\n')}
            onChange={(v) =>
              setTc({ keywords: v.split('\n').map((s) => s.trim()).filter(Boolean) })
            }
            placeholder="buy now\nhello"
          />
        </FormField>
      )}
      {flow.triggerType === 'scheduled' && (
        <ScheduledTriggerEditor
          value={tc.scheduled}
          onChange={(scheduled) => setTc({ scheduled })}
        />
      )}
    </div>
  );
}

function ScheduledTriggerEditor({
  value,
  onChange,
}: {
  value: BotTriggerConfig['scheduled'];
  onChange: (v: NonNullable<BotTriggerConfig['scheduled']>) => void;
}) {
  const cfg = value ?? { frequency: 'daily' as const, atHour: 9, atMinute: 0 };
  const update = (patch: Partial<NonNullable<BotTriggerConfig['scheduled']>>) =>
    onChange({ ...cfg, ...patch } as NonNullable<BotTriggerConfig['scheduled']>);

  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  return (
    <div className="space-y-2 rounded-lg border border-info/20 bg-info-soft/30 p-3">
      <FormField label="Frequency">
        <SelectDropdown
          value={cfg.frequency}
          onChange={(v) => update({ frequency: v as NonNullable<BotTriggerConfig['scheduled']>['frequency'] })}
          options={[
            { value: 'hourly', label: 'Every hour' },
            { value: 'daily', label: 'Every day' },
            { value: 'weekly', label: 'Every week' },
            { value: 'monthly', label: 'Every month' },
          ]}
        />
      </FormField>

      {cfg.frequency !== 'hourly' && (
        <FormField label="At time (UTC)">
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={0} max={23}
              value={cfg.atHour ?? 9}
              onChange={(e) => update({ atHour: Math.max(0, Math.min(23, Number(e.target.value || 0))) })}
              className="w-16 rounded-lg border border-border bg-surface px-2 py-1.5 text-sm text-text-primary focus:outline-none focus:border-primary"
            />
            <span className="text-text-muted">:</span>
            <input
              type="number"
              min={0} max={59}
              value={cfg.atMinute ?? 0}
              onChange={(e) => update({ atMinute: Math.max(0, Math.min(59, Number(e.target.value || 0))) })}
              className="w-16 rounded-lg border border-border bg-surface px-2 py-1.5 text-sm text-text-primary focus:outline-none focus:border-primary"
            />
          </div>
        </FormField>
      )}

      {cfg.frequency === 'hourly' && (
        <FormField label="At minute" hint="0–59 — fires at this minute every hour">
          <input
            type="number"
            min={0} max={59}
            value={cfg.atMinute ?? 0}
            onChange={(e) => update({ atMinute: Math.max(0, Math.min(59, Number(e.target.value || 0))) })}
            className="w-20 rounded-lg border border-border bg-surface px-2 py-1.5 text-sm text-text-primary focus:outline-none focus:border-primary"
          />
        </FormField>
      )}

      {cfg.frequency === 'weekly' && (
        <FormField label="Day of week">
          <SelectDropdown
            value={String(cfg.dayOfWeek ?? 1)}
            onChange={(v) => update({ dayOfWeek: Number(v) })}
            options={dayNames.map((label, value) => ({ value: String(value), label }))}
          />
        </FormField>
      )}

      {cfg.frequency === 'monthly' && (
        <FormField label="Day of month" hint="1–31 — months without that day clamp to month-end">
          <input
            type="number"
            min={1} max={31}
            value={cfg.dayOfMonth ?? 1}
            onChange={(e) => update({ dayOfMonth: Math.max(1, Math.min(31, Number(e.target.value || 1))) })}
            className="w-20 rounded-lg border border-border bg-surface px-2 py-1.5 text-sm text-text-primary focus:outline-none focus:border-primary"
          />
        </FormField>
      )}

      <FormField label="Audience contact UIDs (comma-separated)" hint="Or use group UIDs below — both are unioned">
        <Textarea
          rows={2}
          value={(cfg.audienceContactUids ?? []).join(', ')}
          onChange={(v) => update({ audienceContactUids: v.split(',').map((s) => s.trim()).filter(Boolean) })}
          placeholder="contact_uid_1, contact_uid_2"
        />
      </FormField>

      <FormField label="Audience group UIDs (comma-separated)">
        <Textarea
          rows={2}
          value={(cfg.audienceGroupUids ?? []).join(', ')}
          onChange={(v) => update({ audienceGroupUids: v.split(',').map((s) => s.trim()).filter(Boolean) })}
          placeholder="group_uid_1, group_uid_2"
        />
      </FormField>

      {cfg.nextRunAt && (
        <p className="text-2xs text-text-muted">
          Next run: <span className="font-mono text-text-secondary">{new Date(cfg.nextRunAt).toLocaleString()}</span>
        </p>
      )}
    </div>
  );
}

// ─── Template picker modal ────────────────────────────────────────────────────

interface TemplatePickerModalProps {
  open: boolean;
  templates: Template[];
  selectedUid?: string;
  onClose: () => void;
  onSelect: (uid: string) => void;
}

function TemplatePickerModal({ open, templates, selectedUid, onClose, onSelect }: TemplatePickerModalProps) {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<'all' | 'marketing' | 'utility' | 'authentication'>('all');
  // Default ON only when at least one template in this account has the chatbot tag;
  // otherwise the user would see an empty list with no idea why.
  const hasAnyChatbotTagged = useMemo(
    () => templates.some((t) => (t.useCases ?? []).includes('chatbot')),
    [templates],
  );
  const [chatbotOnly, setChatbotOnly] = useState(hasAnyChatbotTagged);
  useEffect(() => {
    if (hasAnyChatbotTagged) setChatbotOnly(true);
  }, [hasAnyChatbotTagged]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return templates.filter((t) => {
      if (chatbotOnly && !(t.useCases ?? []).includes('chatbot')) return false;
      if (category !== 'all' && t.category !== category) return false;
      if (!q) return true;
      return (
        t.name.toLowerCase().includes(q) ||
        (t.body ?? '').toLowerCase().includes(q)
      );
    });
  }, [templates, search, category, chatbotOnly]);

  return (
    <Modal open={open} onClose={onClose} title="Choose a WhatsApp template" widthClass="max-w-2xl">
      {/* Filters */}
      <div className="mb-3 flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search size={13} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <input
            type="text"
            placeholder="Search templates by name or body…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-xl border border-border bg-surface py-2 pl-9 pr-3 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
          />
        </div>
        <div className="inline-flex rounded-xl border border-border bg-surface p-0.5 self-start">
          {(['all', 'marketing', 'utility', 'authentication'] as const).map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCategory(c)}
              className={clsx(
                'rounded-lg px-2.5 py-1 text-[11px] font-medium capitalize transition-colors',
                category === c ? 'bg-primary-soft text-primary' : 'text-text-muted hover:text-text-primary',
              )}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      {/* Chatbot-only toggle row */}
      <div className="mb-3 flex items-center justify-between rounded-lg border border-border bg-surface-alt/50 px-3 py-1.5 text-[11px] text-text-secondary">
        <span className="flex items-center gap-1.5">
          <Bot size={12} className="text-primary" />
          {hasAnyChatbotTagged
            ? 'Showing templates tagged for chatbot use'
            : 'No templates are tagged for chatbot use yet. Mark some in the Templates section.'}
        </span>
        <label className="flex items-center gap-1.5 cursor-pointer select-none">
          <span className="font-medium">Chatbot only</span>
          <button
            type="button"
            role="switch"
            aria-checked={chatbotOnly}
            onClick={() => setChatbotOnly((v) => !v)}
            className={clsx(
              'relative inline-flex h-4 w-7 items-center rounded-full transition-colors',
              chatbotOnly ? 'bg-primary' : 'bg-border',
            )}
          >
            <span
              className={clsx(
                'inline-block h-3 w-3 rounded-full bg-white shadow-soft transition-transform',
                chatbotOnly ? 'translate-x-3.5' : 'translate-x-0.5',
              )}
            />
          </button>
        </label>
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-surface-alt py-10 text-center text-sm text-text-muted">
          {templates.length === 0
            ? 'No templates available for this WhatsApp account. Create one in the Templates section first.'
            : 'No templates match your filters.'}
        </div>
      ) : (
        <div className="max-h-[420px] overflow-y-auto pr-1 space-y-2">
          {filtered.map((t) => {
            const isSelected = t.templateUid === selectedUid;
            return (
              <button
                key={t.templateUid}
                type="button"
                onClick={() => onSelect(t.templateUid)}
                className={clsx(
                  'w-full text-left rounded-xl border p-3 transition-all',
                  isSelected
                    ? 'border-primary bg-primary-soft/40 ring-2 ring-primary/30'
                    : 'border-border bg-surface hover:border-primary/40 hover:bg-surface-alt',
                )}
              >
                <div className="flex items-start gap-2.5">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary">
                    <FileText size={14} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm text-text-primary truncate">{t.name}</span>
                      <span className="rounded-full bg-surface-alt px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-text-muted">
                        {t.category}
                      </span>
                      <span className="rounded-full bg-surface-alt px-1.5 py-0.5 text-[9px] font-mono text-text-muted">
                        {t.language}
                      </span>
                      {isSelected && (
                        <span className="ml-auto inline-flex items-center gap-0.5 rounded-full bg-primary text-white px-1.5 py-0.5 text-[9px] font-bold">
                          <CheckCircle size={9} /> Selected
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-text-secondary line-clamp-2 leading-relaxed">{t.body}</p>
                    {t.buttons?.length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {t.buttons.map((b, i) => (
                          <span
                            key={i}
                            className="inline-flex items-center rounded-full border border-primary/30 bg-primary-soft/50 px-1.5 py-0.5 text-[10px] font-medium text-primary"
                          >
                            {b.text}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </Modal>
  );
}

// ─── State props panel ────────────────────────────────────────────────────────

interface StatePropsPanel {
  state: BotState;
  allStates: BotState[];
  templates: Template[];
  onChange: (updated: BotState) => void;
  onDelete: (id: string) => void;
}

function StatePropPanel({ state, allStates, templates, onChange, onDelete }: StatePropsPanel) {
  const stateOptions = allStates
    .filter((s) => s.id !== state.id)
    .map((s) => ({ value: s.id, label: s.id }));

  const patch = (p: Partial<BotState>) => onChange({ ...state, ...p } as BotState);

  const [pickerOpen, setPickerOpen] = useState(false);
  const selectedTemplate = state.type === 'message' && state.templateUid
    ? templates.find((t) => t.templateUid === state.templateUid)
    : undefined;

  const saveAsKeys: string[] = useMemo(() => {
    const keys: string[] = [];
    for (const s of allStates) {
      const rec = s as unknown as Record<string, unknown>;
      if (rec.saveAs) {
        keys.push(rec.saveAs as string);
      }
    }
    return keys;
  }, [allStates]);

  const promptWithVarHelper = (value: string, onChangeFn: (v: string) => void) => {
    const suggestVars = ['{{profileName}}', '{{phone}}', ...saveAsKeys.map((k) => `{{${k}}}`)];
    return (
      <div className="space-y-1">
        <Textarea value={value} onChange={onChangeFn} placeholder="Message text..." rows={3} />
        <div className="flex flex-wrap gap-1">
          {suggestVars.map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => onChangeFn(value + v)}
              className="rounded-md bg-primary-soft px-2 py-0.5 text-[10px] font-mono text-primary hover:brightness-110 transition"
            >
              {v}
            </button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-0 h-full overflow-y-auto">
      <div className="px-4 pt-4 pb-2 border-b border-border sticky top-0 bg-surface z-10">
        <p className="text-xs font-semibold text-text-muted uppercase tracking-wider">
          State: {state.id}
        </p>
        <div className="mt-1 flex items-center gap-1.5">
          {STATE_ICONS[state.type as StateTypeKey]}
          <span className="text-sm font-semibold text-text-primary capitalize">{state.type}</span>
        </div>
      </div>
      <div className="p-4 space-y-3 flex-1">
        {/* State ID */}
        <FormField label="State ID" hint="unique">
          <Input
            value={state.id}
            onChange={(e) => {
              const newId = e.target.value;
              // Rename refs in all states
              const updatedState = { ...state, id: newId };
              onChange(updatedState as BotState);
            }}
          />
        </FormField>

        {state.type === 'ai' && <>
          <div className="rounded-xl border border-primary/20 bg-primary-soft/40 p-3 text-xs leading-relaxed text-text-secondary">This step uses your company's configured AI model. <a href="/settings/ai" className="font-medium text-primary underline">Manage AI settings</a></div>
          <FormField label="Assistant instructions" hint="Describe your business, what to ask, and when to involve a person.">
            <div className="mb-2 flex justify-end"><EnhancePromptButton value={state.systemPrompt} onChange={value => patch({ systemPrompt: value })} context="WhatsApp chatbot assistant instructions" /></div>
            <Textarea rows={7} value={state.systemPrompt} onChange={value => patch({ systemPrompt: value })} placeholder="You help customers choose a service. Ask about their needs, answer using our business information, and offer a human when needed." />
          </FormField>
          <FormField label="Conversation outcomes" hint="Comma-separated intent names that end this AI step.">
            <Input value={state.endIntents.join(', ')} onChange={event => patch({ endIntents: event.target.value.split(',').map(intent => intent.trim()).filter(Boolean) })} placeholder="interested, human_requested" />
          </FormField>
          {state.endIntents.map(intent => <FormField key={intent} label={`When ${intent.replace(/_/g, ' ')}`}>
            <SelectDropdown value={state.intentToNextState[intent] ?? ''} onChange={value => patch({ intentToNextState: { ...state.intentToNextState, [intent]: value } })} options={allStates.filter(item => item.id !== state.id).map(item => ({ value: item.id, label: `${item.id} (${item.type})` }))} placeholder="Choose next step" />
          </FormField>)}
          <FormField label="Fallback / handoff step" hint="Used when the conversation reaches its turn limit.">
            <SelectDropdown value={state.fallbackStateId ?? ''} onChange={value => patch({ fallbackStateId: value })} options={allStates.filter(item => item.id !== state.id).map(item => ({ value: item.id, label: `${item.id} (${item.type})` }))} placeholder="Choose a handoff step" />
          </FormField>
          <FormField label="Maximum conversation turns"><Input type="number" min={1} max={50} value={state.maxTurnsBeforeHandoff ?? 10} onChange={event => patch({ maxTurnsBeforeHandoff: Math.max(1, Math.min(50, Number(event.target.value) || 10)) })} /></FormField>
          <FormField label="Save transcript as" hint="Optional custom field key"><Input value={state.saveTranscriptAs ?? ''} onChange={event => patch({ saveTranscriptAs: event.target.value })} placeholder="ai_conversation" /></FormField>
        </>}

        {/* Message */}
        {state.type === 'message' && (
          <>
            <FormField label="WhatsApp template" hint="Optional — uses prompt below if none">
              {selectedTemplate ? (
                <div className="rounded-xl border border-primary/30 bg-primary-soft/30 p-2.5">
                  <div className="flex items-start gap-2">
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary">
                      <FileText size={13} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-[12px] text-text-primary truncate">{selectedTemplate.name}</div>
                      <p className="text-[11px] text-text-secondary line-clamp-2 mt-0.5">{selectedTemplate.body}</p>
                      {selectedTemplate.buttons?.length > 0 && (
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          {selectedTemplate.buttons.map((b, i) => (
                            <span key={i} className="inline-flex items-center rounded-full border border-primary/30 bg-surface px-1.5 py-0.5 text-[9px] font-medium text-primary">
                              {b.text}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="mt-2 flex gap-1.5">
                    <Button size="sm" variant="secondary" className="flex-1" onClick={() => setPickerOpen(true)}>
                      Change
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => patch({ templateUid: undefined } as Partial<BotState>)}
                    >
                      <X size={12} />
                    </Button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setPickerOpen(true)}
                  className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-primary/40 bg-primary-soft/20 px-3 py-2.5 text-[12px] font-semibold text-primary hover:bg-primary-soft/40 transition-colors"
                >
                  <FileText size={13} /> Choose template
                </button>
              )}
            </FormField>
            <FormField label="Prompt" hint={selectedTemplate ? 'Ignored — template is set' : 'Used if no template'}>
              {promptWithVarHelper(state.prompt ?? '', (v) => patch({ prompt: v } as Partial<BotState>))}
            </FormField>
            <FormField label="Next state">
              <SelectDropdown
                value={state.next ?? ''}
                onChange={(v) => patch({ next: v || undefined } as Partial<BotState>)}
                options={stateOptions}
                placeholder="Terminal (no next)"
              />
            </FormField>

            <TemplatePickerModal
              open={pickerOpen}
              templates={templates}
              selectedUid={state.templateUid}
              onClose={() => setPickerOpen(false)}
              onSelect={(uid) => {
                patch({ templateUid: uid } as Partial<BotState>);
                setPickerOpen(false);
              }}
            />
          </>
        )}

        {/* Choice */}
        {state.type === 'choice' && (() => {
          const presentation = state.presentationType ?? 'buttons';
          // Keep the flat options[] mirror in sync with sections[].rows so the
          // text-fallback matcher (server-side and on legacy clients) still works.
          const flattenSections = (secs: ChoiceSection[] = []): ChoiceOption[] =>
            secs.flatMap((s) => s.rows);

          return (
          <>
            <FormField label="Presentation">
              <div className="grid grid-cols-3 gap-1 rounded-lg border border-border bg-surface-alt p-0.5">
                {([
                  { key: 'buttons' as const, label: 'Buttons', icon: <ListChecks size={12} /> },
                  { key: 'media_buttons' as const, label: 'Media', icon: <Image size={12} /> },
                  { key: 'list' as const, label: 'List', icon: <Rows3 size={12} /> },
                ]).map((opt) => {
                  const active = presentation === opt.key;
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => {
                        if (opt.key === presentation) return;
                        if (opt.key === 'list') {
                          // Move flat options into a single starter section
                          const rows = state.options ?? [];
                          const sections: ChoiceSection[] = (state.sections?.length ? state.sections : [{ title: 'Options', rows }]);
                          patch({
                            presentationType: 'list',
                            sections,
                            options: flattenSections(sections),
                            buttonText: state.buttonText ?? 'View options',
                          } as Partial<BotState>);
                        } else if (opt.key === 'media_buttons') {
                          patch({
                            presentationType: 'media_buttons',
                            mediaType: state.mediaType ?? 'image',
                            sections: undefined,
                          } as Partial<BotState>);
                        } else {
                          // buttons — flatten sections back into options
                          const flat = state.sections ? flattenSections(state.sections) : (state.options ?? []);
                          patch({
                            presentationType: 'buttons',
                            options: flat,
                            sections: undefined,
                            mediaUrl: undefined,
                            mediaType: undefined,
                            mediaFilename: undefined,
                          } as Partial<BotState>);
                        }
                      }}
                      className={clsx(
                        'flex items-center justify-center gap-1 rounded-md py-1 text-[11px] font-medium transition',
                        active ? 'bg-surface text-text-primary shadow-soft' : 'text-text-muted hover:text-text-primary',
                      )}
                    >
                      {opt.icon} {opt.label}
                    </button>
                  );
                })}
              </div>
            </FormField>

            <FormField label="Prompt / Body">
              {promptWithVarHelper(state.prompt, (v) => patch({ prompt: v } as Partial<BotState>))}
            </FormField>

            {/* Media block — only for media_buttons */}
            {presentation === 'media_buttons' && (
              <div className="space-y-2 rounded-lg border border-primary/30 bg-primary-soft/20 p-2.5">
                <div className="flex items-center gap-1.5 text-[11px] font-semibold text-primary">
                  <Image size={11} /> MEDIA HEADER
                </div>
                <FormField label="Media type">
                  <SelectDropdown
                    value={state.mediaType ?? 'image'}
                    onChange={(v) => patch({ mediaType: v as ChoiceMediaType } as Partial<BotState>)}
                    options={[
                      { value: 'image', label: 'Image' },
                      { value: 'video', label: 'Video' },
                      { value: 'document', label: 'Document' },
                    ]}
                  />
                </FormField>
                <FormField label="Media URL" hint="Public HTTPS URL (jpg/png/mp4/pdf)">
                  <Input
                    placeholder="https://…"
                    value={state.mediaUrl ?? ''}
                    onChange={(e) => patch({ mediaUrl: e.target.value || undefined } as Partial<BotState>)}
                  />
                </FormField>
                {state.mediaType === 'document' && (
                  <FormField label="Filename (shown on WhatsApp)">
                    <Input
                      placeholder="brochure.pdf"
                      value={state.mediaFilename ?? ''}
                      onChange={(e) => patch({ mediaFilename: e.target.value || undefined } as Partial<BotState>)}
                    />
                  </FormField>
                )}
              </div>
            )}

            {/* Header & footer text — list / media_buttons */}
            {(presentation === 'list' || presentation === 'media_buttons') && (
              <>
                <FormField label="Header text" hint="Optional, up to 60 chars">
                  <Input
                    placeholder="Optional bold header"
                    value={state.headerText ?? ''}
                    onChange={(e) => patch({ headerText: e.target.value || undefined } as Partial<BotState>)}
                  />
                </FormField>
                <FormField label="Footer text" hint="Optional, small grey text">
                  <Input
                    placeholder="Optional footer note"
                    value={state.footerText ?? ''}
                    onChange={(e) => patch({ footerText: e.target.value || undefined } as Partial<BotState>)}
                  />
                </FormField>
              </>
            )}

            {/* List CTA label */}
            {presentation === 'list' && (
              <FormField label="Open-menu button label">
                <Input
                  placeholder="View options"
                  value={state.buttonText ?? ''}
                  onChange={(e) => patch({ buttonText: e.target.value || 'View options' } as Partial<BotState>)}
                />
              </FormField>
            )}

            {/* Options editor — buttons / media_buttons (max 3) */}
            {(presentation === 'buttons' || presentation === 'media_buttons') && (
              <FormField label={presentation === 'media_buttons' ? 'Buttons (max 3)' : 'Options'}>
                <div className="space-y-2">
                  {(state.options ?? []).map((opt, i) => (
                    <div key={i} className="rounded-lg border border-border bg-surface-alt p-2 space-y-1.5">
                      <div className="flex items-center gap-2">
                        <Input
                          placeholder="Label"
                          value={opt.label}
                          onChange={(e) => {
                            const opts = [...state.options];
                            opts[i] = { ...opts[i], label: e.target.value };
                            patch({ options: opts } as Partial<BotState>);
                          }}
                        />
                        <Input
                          placeholder="Value"
                          value={opt.value}
                          onChange={(e) => {
                            const opts = [...state.options];
                            opts[i] = { ...opts[i], value: e.target.value };
                            patch({ options: opts } as Partial<BotState>);
                          }}
                        />
                        <button
                          onClick={() => {
                            const opts = state.options.filter((_, j) => j !== i);
                            patch({ options: opts } as Partial<BotState>);
                          }}
                          className="text-text-muted hover:text-danger transition shrink-0"
                        >
                          <X size={14} />
                        </button>
                      </div>
                      <SelectDropdown
                        value={opt.nextStateId}
                        onChange={(v) => {
                          const opts = [...state.options];
                          opts[i] = { ...opts[i], nextStateId: v };
                          patch({ options: opts } as Partial<BotState>);
                        }}
                        options={stateOptions}
                        placeholder="Next state..."
                      />
                    </div>
                  ))}
                  <Button
                    variant="ghost"
                    size="sm"
                    iconLeft={<Plus size={12} />}
                    disabled={presentation === 'media_buttons' && (state.options?.length ?? 0) >= 3}
                    onClick={() => {
                      const newOpt: ChoiceOption = { label: '', value: '', nextStateId: '' };
                      patch({ options: [...(state.options ?? []), newOpt] } as Partial<BotState>);
                    }}
                  >
                    Add {presentation === 'media_buttons' ? 'button' : 'option'}
                  </Button>
                </div>
              </FormField>
            )}

            {/* Sections editor — list */}
            {presentation === 'list' && (
              <FormField label="Sections" hint="Each section can have a title and up to 10 rows. Max 10 sections.">
                <div className="space-y-3">
                  {(state.sections ?? []).map((sec, si) => {
                    const updateSection = (next: Partial<ChoiceSection>) => {
                      const sections = [...(state.sections ?? [])];
                      sections[si] = { ...sec, ...next };
                      patch({ sections, options: flattenSections(sections) } as Partial<BotState>);
                    };
                    return (
                      <div key={si} className="rounded-xl border border-border bg-surface p-2.5 space-y-2">
                        <div className="flex items-center gap-2">
                          <Input
                            placeholder="Section title (optional)"
                            value={sec.title ?? ''}
                            onChange={(e) => updateSection({ title: e.target.value || undefined })}
                          />
                          <button
                            onClick={() => {
                              const sections = (state.sections ?? []).filter((_, j) => j !== si);
                              patch({ sections, options: flattenSections(sections) } as Partial<BotState>);
                            }}
                            className="text-text-muted hover:text-danger transition shrink-0"
                            title="Remove section"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>

                        <div className="space-y-1.5 pl-1">
                          {(sec.rows ?? []).map((row, ri) => (
                            <div key={ri} className="rounded-lg border border-border bg-surface-alt p-2 space-y-1.5">
                              <div className="flex items-center gap-2">
                                <Input
                                  placeholder="Row label"
                                  value={row.label}
                                  onChange={(e) => {
                                    const rows = [...sec.rows];
                                    rows[ri] = { ...row, label: e.target.value };
                                    updateSection({ rows });
                                  }}
                                />
                                <Input
                                  placeholder="Value"
                                  value={row.value}
                                  onChange={(e) => {
                                    const rows = [...sec.rows];
                                    rows[ri] = { ...row, value: e.target.value };
                                    updateSection({ rows });
                                  }}
                                />
                                <button
                                  onClick={() => {
                                    const rows = sec.rows.filter((_, j) => j !== ri);
                                    updateSection({ rows });
                                  }}
                                  className="text-text-muted hover:text-danger transition shrink-0"
                                >
                                  <X size={14} />
                                </button>
                              </div>
                              <SelectDropdown
                                value={row.nextStateId}
                                onChange={(v) => {
                                  const rows = [...sec.rows];
                                  rows[ri] = { ...row, nextStateId: v };
                                  updateSection({ rows });
                                }}
                                options={stateOptions}
                                placeholder="Next state..."
                              />
                            </div>
                          ))}
                          <Button
                            variant="ghost"
                            size="sm"
                            iconLeft={<Plus size={12} />}
                            disabled={(sec.rows?.length ?? 0) >= 10}
                            onClick={() => {
                              const rows = [...(sec.rows ?? []), { label: '', value: '', nextStateId: '' }];
                              updateSection({ rows });
                            }}
                          >
                            Add row
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                  <Button
                    variant="ghost"
                    size="sm"
                    iconLeft={<Plus size={12} />}
                    disabled={(state.sections?.length ?? 0) >= 10}
                    onClick={() => {
                      const sections = [...(state.sections ?? []), { title: '', rows: [] }];
                      patch({ sections, options: flattenSections(sections) } as Partial<BotState>);
                    }}
                  >
                    Add section
                  </Button>
                </div>
              </FormField>
            )}

            <FormField label="Save answer as">
              <Input
                placeholder="e.g. category"
                value={state.saveAs ?? ''}
                onChange={(e) => patch({ saveAs: e.target.value || undefined } as Partial<BotState>)}
              />
            </FormField>
            <FormField label="Retry prompt">
              <Textarea
                rows={2}
                value={state.retryPrompt ?? ''}
                onChange={(v) => patch({ retryPrompt: v || undefined } as Partial<BotState>)}
                placeholder="Please pick one of the options..."
              />
            </FormField>
          </>
          );
        })()}

        {/* Collect */}
        {state.type === 'collect' && (
          <>
            <FormField label="Prompt">
              {promptWithVarHelper(state.prompt, (v) => patch({ prompt: v } as Partial<BotState>))}
            </FormField>
            <FormField label="Expected type">
              <SelectDropdown
                value={state.expectedType}
                onChange={(v) => patch({ expectedType: v as CollectState['expectedType'] } as Partial<BotState>)}
                options={[
                  { value: 'text', label: 'Text' },
                  { value: 'phone', label: 'Phone' },
                  { value: 'email', label: 'Email' },
                  { value: 'number', label: 'Number' },
                  { value: 'image', label: 'Image' },
                ]}
              />
            </FormField>
            <FormField label="Save as" required>
              <Input
                value={state.saveAs}
                onChange={(e) => patch({ saveAs: e.target.value } as Partial<BotState>)}
                placeholder="e.g. budget"
              />
            </FormField>
            <FormField label="Next state" required>
              <SelectDropdown
                value={state.next}
                onChange={(v) => patch({ next: v } as Partial<BotState>)}
                options={stateOptions}
                placeholder="Select next state..."
              />
            </FormField>
            {(state.expectedType === 'text' || state.expectedType === 'number') && (
              <FormField label="Validation">
                <div className="space-y-1.5">
                  {state.expectedType === 'text' && (
                    <Input
                      placeholder="Regex (optional)"
                      value={state.validation?.regex ?? ''}
                      onChange={(e) =>
                        patch({ validation: { ...state.validation, regex: e.target.value || undefined } } as Partial<BotState>)
                      }
                    />
                  )}
                  {state.expectedType === 'number' && (
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        placeholder="Min"
                        value={state.validation?.min ?? ''}
                        onChange={(e) =>
                          patch({ validation: { ...state.validation, min: e.target.value ? Number(e.target.value) : undefined } } as Partial<BotState>)
                        }
                      />
                      <Input
                        type="number"
                        placeholder="Max"
                        value={state.validation?.max ?? ''}
                        onChange={(e) =>
                          patch({ validation: { ...state.validation, max: e.target.value ? Number(e.target.value) : undefined } } as Partial<BotState>)
                        }
                      />
                    </div>
                  )}
                </div>
              </FormField>
            )}
            <FormField label="Retry prompt">
              <Textarea
                rows={2}
                value={state.retryPrompt ?? ''}
                onChange={(v) => patch({ retryPrompt: v || undefined } as Partial<BotState>)}
                placeholder="Invalid input, please try again..."
              />
            </FormField>
          </>
        )}

        {/* Condition */}
        {state.type === 'condition' && (
          <>
            <FormField label="Branches">
              <div className="space-y-2">
                {(state.branches ?? []).map((branch, i) => (
                  <div key={i} className="rounded-lg border border-border bg-surface-alt p-2 space-y-1.5">
                    <div className="flex items-center gap-2">
                      <Input
                        placeholder='e.g. budget >= 1000'
                        value={branch.when}
                        onChange={(e) => {
                          const bs = [...state.branches];
                          bs[i] = { ...bs[i], when: e.target.value };
                          patch({ branches: bs } as Partial<BotState>);
                        }}
                      />
                      <button
                        onClick={() => {
                          const bs = state.branches.filter((_, j) => j !== i);
                          patch({ branches: bs } as Partial<BotState>);
                        }}
                        className="text-text-muted hover:text-danger transition shrink-0"
                      >
                        <X size={14} />
                      </button>
                    </div>
                    <SelectDropdown
                      value={branch.nextStateId}
                      onChange={(v) => {
                        const bs = [...state.branches];
                        bs[i] = { ...bs[i], nextStateId: v };
                        patch({ branches: bs } as Partial<BotState>);
                      }}
                      options={stateOptions}
                      placeholder="Next state..."
                    />
                  </div>
                ))}
                <Button
                  variant="ghost"
                  size="sm"
                  iconLeft={<Plus size={12} />}
                  onClick={() => {
                    const newBranch: ConditionBranch = { when: '', nextStateId: '' };
                    patch({ branches: [...(state.branches ?? []), newBranch] } as Partial<BotState>);
                  }}
                >
                  Add branch
                </Button>
              </div>
            </FormField>
            <FormField label="Fallback state (else)">
              <SelectDropdown
                value={state.fallbackStateId}
                onChange={(v) => patch({ fallbackStateId: v } as Partial<BotState>)}
                options={stateOptions}
                placeholder="Select fallback..."
              />
            </FormField>
          </>
        )}

        {/* Handoff */}
        {state.type === 'handoff' && (
          <>
            <FormField label="Assign to">
              <SelectDropdown
                value={state.assignTo.startsWith('specific:') ? 'specific' : state.assignTo}
                onChange={(v) => patch({ assignTo: v } as Partial<BotState>)}
                options={[
                  { value: 'unassigned', label: 'Unassigned' },
                  { value: 'round_robin', label: 'Round robin' },
                  { value: 'specific', label: 'Specific user' },
                ]}
              />
            </FormField>
            {state.assignTo.startsWith('specific') && (
              <FormField label="User UID">
                <Input
                  value={state.assignTo.startsWith('specific:') ? state.assignTo.slice(9) : ''}
                  onChange={(e) => patch({ assignTo: `specific:${e.target.value}` } as Partial<BotState>)}
                  placeholder="user UID..."
                />
              </FormField>
            )}
            <FormField label="Notify message">
              <Textarea
                rows={2}
                value={state.notifyMessage ?? ''}
                onChange={(v) => patch({ notifyMessage: v || undefined } as Partial<BotState>)}
                placeholder="You've been connected to a human agent..."
              />
            </FormField>
          </>
        )}

        {/* End */}
        {state.type === 'end' && (
          <FormField label="Terminal status">
            <SelectDropdown
              value={state.terminalStatus ?? 'qualified'}
              onChange={(v) => patch({ terminalStatus: v as EndState['terminalStatus'] } as Partial<BotState>)}
              options={[
                { value: 'qualified', label: 'Qualified' },
                { value: 'lost', label: 'Lost' },
                { value: 'spam', label: 'Spam' },
              ]}
            />
          </FormField>
        )}
      </div>
      <div className="px-4 py-3 border-t border-border sticky bottom-0 bg-surface">
        <Button
          variant="danger"
          size="sm"
          iconLeft={<Trash2 size={13} />}
          className="w-full"
          onClick={() => {
            if (window.confirm(`Delete state "${state.id}"?`)) onDelete(state.id);
          }}
        >
          Delete state
        </Button>
      </div>
    </div>
  );
}

// ─── Flow Start virtual node ──────────────────────────────────────────────────

const TRIGGER_ICONS: Record<BotTriggerType, React.ReactNode> = {
  keyword: <KeyRound size={14} />,
  ad: <Megaphone size={14} />,
  lead_ad_form: <FileSearch size={14} />,
  manual: <Zap size={14} />,
  scheduled: <Calendar size={14} />,
};

interface FlowStartNodeData {
  triggerType: BotTriggerType;
  summary: string;
  [key: string]: unknown;
}

function FlowStartNode({ data }: { data: FlowStartNodeData }) {
  const icon = TRIGGER_ICONS[data.triggerType] ?? <Zap size={14} />;
  return (
    <div className="relative rounded-2xl border-2 border-dashed border-primary bg-primary-soft shadow-soft min-w-[200px] max-w-[230px]">
      <div className="flex items-center gap-1.5 rounded-t-xl px-3 py-2 bg-primary-soft text-primary">
        <Zap size={13} />
        <span className="text-[11px] font-semibold uppercase tracking-wider">Flow Start</span>
      </div>
      <div className="px-3 py-2.5">
        <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-text-secondary mb-1">
          {icon}
          <span>{TRIGGER_LABELS[data.triggerType] ?? data.triggerType}</span>
        </div>
        <p className="text-[11px] text-text-secondary line-clamp-3 leading-relaxed whitespace-pre-wrap">
          {data.summary || 'Configure this trigger in the right panel →'}
        </p>
      </div>
      <Handle
        type="source"
        position={Position.Bottom}
        id="start"
        className="!w-3 !h-3 !bg-primary !border-2 !border-surface"
      />
    </div>
  );
}

function triggerSummary(flow: BotFlow): string {
  const tc = flow.triggerConfig ?? {};
  switch (flow.triggerType) {
    case 'keyword':
      if (tc.keywords?.length) return `Fires on: ${tc.keywords.slice(0, 4).join(', ')}${tc.keywords.length > 4 ? ` +${tc.keywords.length - 4}` : ''}`;
      return 'No keywords set yet.';
    case 'ad': {
      const parts: string[] = [];
      if (tc.adIds?.length) parts.push(`${tc.adIds.length} ad${tc.adIds.length > 1 ? 's' : ''}`);
      if (tc.adSetIds?.length) parts.push(`${tc.adSetIds.length} ad set${tc.adSetIds.length > 1 ? 's' : ''}`);
      if (tc.campaignIds?.length) parts.push(`${tc.campaignIds.length} campaign${tc.campaignIds.length > 1 ? 's' : ''}`);
      return parts.length ? `Fires on click of: ${parts.join(', ')}` : 'No ads linked yet.';
    }
    case 'lead_ad_form':
      return tc.formIds?.length ? `${tc.formIds.length} lead form${tc.formIds.length > 1 ? 's' : ''} linked` : 'No lead forms linked.';
    case 'manual':
      return 'Triggered by hand from the lead page.';
    case 'scheduled':
      return tc.scheduled ? `Runs ${tc.scheduled.frequency ?? 'on schedule'}` : 'No schedule set yet.';
    default:
      return '';
  }
}

const FLOW_START_NODE_ID = '__flow_start__';

// ─── Canvas inner (needs access to RF hooks) ──────────────────────────────────

interface CanvasProps {
  flow: BotFlow;
  selectedStateId: string | null;
  onSelectState: (id: string | null) => void;
  onFlowChange: (updated: BotFlow) => void;
  onContextMenu: (e: React.MouseEvent, stateId: string) => void;
  errorsByStateId: Map<string, string[]>;
  warningsByStateId: Map<string, string[]>;
  templates: Template[];
}

const nodeTypes = { botState: FlowNode, flowStart: FlowStartNode };

function CanvasInner({
  flow,
  selectedStateId,
  onSelectState,
  onFlowChange,
  onContextMenu,
  errorsByStateId,
  warningsByStateId,
  templates,
}: CanvasProps) {
  const { fitView } = useReactFlow();

  // Derive initial positions
  const [positions, setPositions] = useState<Map<string, { x: number; y: number }>>(() =>
    autoLayout(flow.states, flow.startStateId),
  );

  const templatesByUid = useMemo(() => {
    const m = new Map<string, { name: string; body: string; buttons: { text: string }[] }>();
    for (const t of templates) {
      m.set(t.templateUid, {
        name: t.name,
        body: t.body ?? '',
        buttons: (t.buttons ?? []).map((b) => ({ text: b.text })),
      });
    }
    return m;
  }, [templates]);

  // React Flow v12 + React 19: use the managed-state hooks instead of the
  // controlled-prop pattern. The controlled `nodes={...}` prop doesn't reliably
  // flow into RF's internal zustand store under React 19 + StrictMode, leaving
  // the canvas blank even though the array is populated.
  const [nodes, setNodes, onNodesChangeRF] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChangeRF] = useEdgesState<Edge>([]);

  // Build nodes from flow.states + positions
  useEffect(() => {
    const stateNodes = stateToNodes(
      flow.states,
      flow.startStateId,
      positions,
      errorsByStateId,
      warningsByStateId,
      onContextMenu,
      templatesByUid,
    );
    const startPos = positions.get(flow.startStateId) ?? { x: 0, y: 0 };
    const flowStartNode: Node = {
      id: FLOW_START_NODE_ID,
      type: 'flowStart',
      position: { x: startPos.x - 320, y: startPos.y },
      data: {
        triggerType: flow.triggerType,
        summary: triggerSummary(flow),
      } as FlowStartNodeData,
      draggable: true,
      deletable: false,
      selectable: false,
    };
    setNodes([flowStartNode, ...stateNodes]);
  }, [flow, positions, errorsByStateId, warningsByStateId, onContextMenu, templatesByUid, setNodes]);

  // Build edges from flow.states
  useEffect(() => {
    const stateEdges = statesToEdges(flow.states);
    if (flow.startStateId) {
      stateEdges.unshift({
        id: `${FLOW_START_NODE_ID}->${flow.startStateId}`,
        source: FLOW_START_NODE_ID,
        sourceHandle: 'start',
        target: flow.startStateId,
        type: 'default',
        animated: true,
        style: { stroke: 'var(--color-primary)', strokeDasharray: '6 4', strokeWidth: 2 },
        deletable: false,
      });
    }
    setEdges(stateEdges);
  }, [flow.states, flow.startStateId, setEdges]);

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => {
      onNodesChangeRF(changes);
      // Only commit position to our `positions` map when the drag has ended
      // (dragging === false) or when the change is programmatic (dragging === undefined).
      // Updating `positions` mid-drag (dragging === true) re-triggers the node-rebuild
      // effect, which replaces the entire nodes array and visually collapses the
      // other nodes while you're dragging.
      const newPositions = new Map(positions);
      let changed = false;
      for (const ch of changes) {
        if (
          ch.type === 'position' &&
          ch.position &&
          ch.dragging !== true &&
          ch.id !== FLOW_START_NODE_ID
        ) {
          newPositions.set(ch.id, ch.position);
          changed = true;
        }
      }
      if (changed) setPositions(newPositions);
    },
    [onNodesChangeRF, positions],
  );

  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      // Deletions — remove the corresponding next/option ref
      for (const change of changes) {
        if (change.type === 'remove') {
          const edgeId = change.id;
          // Format: `${sourceId}->${handleId}`
          const [sourceId, handleId] = edgeId.split('->');
          const stateIdx = flow.states.findIndex((s) => s.id === sourceId);
          if (stateIdx === -1) continue;
          const state = { ...flow.states[stateIdx] } as BotState;
          const stateRec = state as unknown as Record<string, unknown>;
          if (handleId === 'next') {
            stateRec.next = undefined;
          } else if (handleId?.startsWith('option_')) {
            const optIdx = parseInt(handleId.split('_')[1], 10);
            if (state.type === 'choice') {
              const opts = [...state.options];
              opts[optIdx] = { ...opts[optIdx], nextStateId: '' };
              stateRec.options = opts;
            }
          } else if (handleId?.startsWith('branch_')) {
            const brIdx = parseInt(handleId.split('_')[1], 10);
            if (state.type === 'condition') {
              const bs = [...state.branches];
              bs[brIdx] = { ...bs[brIdx], nextStateId: '' };
              stateRec.branches = bs;
            }
          } else if (handleId?.startsWith('intent:') && state.type === 'ai') {
            state.intentToNextState = { ...state.intentToNextState, [handleId.slice(7)]: '' };
          } else if (handleId === 'fallback') {
            if (state.type === 'condition' || state.type === 'ai') {
              stateRec.fallbackStateId = '';
            }
          }
          const newStates = [...flow.states];
          newStates[stateIdx] = state;
          onFlowChange({ ...flow, states: newStates });
        }
      }
      onEdgesChangeRF(changes);
    },
    [flow, onEdgesChangeRF, onFlowChange],
  );

  const onConnect = useCallback(
    (connection: Connection) => {
      const { source, sourceHandle, target } = connection;
      if (!source || !target) return;
      const stateIdx = flow.states.findIndex((s) => s.id === source);
      if (stateIdx === -1) return;
      const state = { ...flow.states[stateIdx] } as BotState;
      const stateRec = state as unknown as Record<string, unknown>;

      if (sourceHandle === 'next' || !sourceHandle) {
        stateRec.next = target;
      } else if (sourceHandle.startsWith('option_')) {
        const optIdx = parseInt(sourceHandle.split('_')[1], 10);
        if (state.type === 'choice') {
          const opts = [...state.options];
          if (opts[optIdx]) opts[optIdx] = { ...opts[optIdx], nextStateId: target };
          stateRec.options = opts;
        }
      } else if (sourceHandle.startsWith('branch_')) {
        const brIdx = parseInt(sourceHandle.split('_')[1], 10);
        if (state.type === 'condition') {
          const bs = [...state.branches];
          if (bs[brIdx]) bs[brIdx] = { ...bs[brIdx], nextStateId: target };
          stateRec.branches = bs;
        }
      } else if (sourceHandle.startsWith('intent:') && state.type === 'ai') {
        state.intentToNextState = { ...state.intentToNextState, [sourceHandle.slice(7)]: target };
      } else if (sourceHandle === 'fallback') {
        if (state.type === 'condition' || state.type === 'ai') {
          stateRec.fallbackStateId = target;
        }
      }

      const newStates = [...flow.states];
      newStates[stateIdx] = state;
      onFlowChange({ ...flow, states: newStates });
      addEdge(connection, edges);
    },
    [flow, edges, onFlowChange],
  );

  const onNodeClick = useCallback(
    (_: React.MouseEvent, node: Node) => {
      onSelectState(node.id === selectedStateId ? null : node.id);
    },
    [selectedStateId, onSelectState],
  );

  const onPaneClick = useCallback(() => {
    onSelectState(null);
  }, [onSelectState]);

  const handleAutoLayout = useCallback(() => {
    const newPositions = autoLayout(flow.states, flow.startStateId);
    setPositions(newPositions);
    setTimeout(() => fitView({ padding: 0.12 }), 50);
  }, [flow, fitView]);

  // Keep positions in sync when states change (new node added)
  useEffect(() => {
    let changed = false;
    const newPositions = new Map(positions);
    for (const s of flow.states) {
      if (!newPositions.has(s.id)) {
        newPositions.set(s.id, { x: Math.random() * 400, y: Math.random() * 300 });
        changed = true;
      }
    }
    if (changed) setPositions(newPositions);
  }, [flow.states, positions]);

  // Re-fit the viewport whenever the node count changes from zero, or when the
  // canvas is opened with wizard-created states. React Flow's `fitView` prop
  // only fires on the very first mount — without this, viewport stays on (0,0)
  // while the auto-laid-out nodes sit at negative X coordinates and look "missing".
  useEffect(() => {
    if (nodes.length === 0) return;
    const t = setTimeout(() => {
      try { fitView({ padding: 0.15, duration: 200 }); } catch { /* RF not mounted yet */ }
    }, 80);
    return () => clearTimeout(t);
  }, [nodes.length, fitView]);

  return (
    <div className="relative h-full">
      {/* Auto-layout button */}
      <div className="absolute top-3 left-3 z-10">
        <Button size="sm" variant="secondary" iconLeft={<LayoutGrid size={13} />} onClick={handleAutoLayout}>
          Auto-layout
        </Button>
      </div>

      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeClick={onNodeClick}
        onPaneClick={onPaneClick}
        deleteKeyCode="Delete"
        fitView
        fitViewOptions={{ padding: 0.12 }}
        minZoom={0.3}
        maxZoom={2}
        className="bg-bg"
      >
        <Background variant={BackgroundVariant.Dots} gap={16} size={1} color="var(--color-border)" />
        <MiniMap
          nodeStrokeWidth={2}
          className="!bg-surface !border !border-border !rounded-xl"
          position="bottom-right"
        />
        <Controls className="!shadow-soft !border !border-border !rounded-xl" position="bottom-left" />
      </ReactFlow>
    </div>
  );
}

// ─── Analytics tab ────────────────────────────────────────────────────────────

interface AnalyticsTabProps {
  flow: BotFlow;
}

function AnalyticsTab({ flow }: AnalyticsTabProps) {
  const [data, setData] = useState<FlowAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    botFlowsService
      .analytics(flow.flowUid)
      .then((d) => { if (!cancelled) { setData(d); setError(null); } })
      .catch(() => { if (!cancelled) setError('Failed to load analytics'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [flow.flowUid]);

  if (loading) {
    return (
      <div className="px-7 py-7 space-y-4">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[...Array(4)].map((_, i) => <div key={i} className="skeleton h-24 rounded-2xl" />)}
        </div>
        <div className="skeleton h-64 rounded-2xl" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-24 text-text-muted">
        <p className="text-sm">{error ?? 'No analytics data'}</p>
      </div>
    );
  }

  const { totals, perState } = data;
  const stateById = new Map(flow.states.map((s) => [s.id, s] as const));
  const maxVisits = perState.reduce((m, s) => Math.max(m, s.visits, s.inFlight), 0);
  const completionPct = totals.completionRate !== null ? (totals.completionRate * 100).toFixed(1) : null;

  return (
    <div className="px-7 py-7 space-y-5 max-w-[1100px] mx-auto">
      <div>
        <h2 className="font-display text-base font-semibold text-text-primary">Performance</h2>
        <p className="mt-0.5 text-xs text-text-muted">
          Last 30 days · across all published versions
        </p>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <KpiTile label="Entered" value={totals.entered} accent="primary" />
        <KpiTile label="Completed" value={totals.completed} accent="success" sub={completionPct ? `${completionPct}%` : undefined} />
        <KpiTile label="In flight" value={totals.inFlight} accent="info" />
        <KpiTile label="Handed off" value={totals.handedOff} accent="warning" />
      </div>

      {totals.errored > 0 && (
        <div className="rounded-xl border border-danger/30 bg-danger-soft px-4 py-3 text-sm text-danger">
          <strong>{totals.errored}</strong> lead{totals.errored === 1 ? '' : 's'} hit an error in this flow. Check the flow validator and recent edits.
        </div>
      )}

      <Card padded>
        <h3 className="mb-5 text-sm font-semibold text-text-primary">Conversation outcomes</h3>
        <div className="h-56" role="img" aria-label={`Entered ${totals.entered}, completed ${totals.completed}, handed off ${totals.handedOff}, errors ${totals.errored}`}>
          <ResponsiveContainer width="100%" height="100%"><BarChart data={[{ name: 'Entered', count: totals.entered }, { name: 'Completed', count: totals.completed }, { name: 'Handed off', count: totals.handedOff }, { name: 'Errors', count: totals.errored }]} barSize={42} margin={{ left: -20 }}>
            <CartesianGrid strokeDasharray="3 5" vertical={false} stroke="#e9e6df" /><XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12 }} /><YAxis allowDecimals={false} axisLine={false} tickLine={false} tick={{ fontSize: 12 }} /><Tooltip /><Bar dataKey="count" fill="#c8a552" radius={[6,6,0,0]} />
          </BarChart></ResponsiveContainer>
        </div>
      </Card>
      <WebhookPanel flowUid={flow.flowUid} />

      {/* Per-state funnel */}
      <div className="rounded-2xl border border-border bg-surface">
        <div className="border-b border-border px-5 py-3">
          <h3 className="font-display text-sm font-semibold text-text-primary">Per-state activity</h3>
          <p className="mt-0.5 text-xs text-text-muted">Visits and how leads exited each state.</p>
        </div>
        {perState.length === 0 ? (
          <div className="px-5 py-8 text-center text-xs text-text-muted">
            No traffic yet. Once leads start hitting this flow you'll see a per-state funnel here.
          </div>
        ) : (
          <div className="divide-y divide-border">
            {perState.map((row) => {
              const state = stateById.get(row.stateId);
              const label = state ? `${row.stateId} (${state.type})` : row.stateId;
              const widthPct = maxVisits > 0 ? Math.max(2, (row.visits / maxVisits) * 100) : 2;
              return (
                <div key={row.stateId} className="px-5 py-3">
                  <div className="flex items-center justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-text-primary">{label}</div>
                      <div className="mt-1 h-1.5 w-full rounded-full bg-surface-alt overflow-hidden">
                        <div
                          className="h-full rounded-full bg-primary transition-all"
                          style={{ width: `${widthPct}%` }}
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-4 shrink-0 text-xs tabular-nums">
                      <Stat label="visits" value={row.visits} />
                      <Stat label="advanced" value={row.advanced} />
                      <Stat label="terminated" value={row.terminated} />
                      <Stat label="in flight" value={row.inFlight} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function KpiTile({ label, value, accent, sub }: {
  label: string;
  value: number;
  accent: 'primary' | 'success' | 'info' | 'warning';
  sub?: string;
}) {
  const accentCls = {
    primary: 'text-primary',
    success: 'text-success',
    info: 'text-info',
    warning: 'text-warning',
  }[accent];
  return (
    <div className="rounded-2xl border border-border bg-surface p-4">
      <div className="text-2xs font-semibold uppercase tracking-[0.14em] text-text-muted">{label}</div>
      <div className={`mt-1 font-display text-2xl font-bold tabular-nums ${accentCls}`}>{value.toLocaleString()}</div>
      {sub && <div className="mt-0.5 text-2xs text-text-muted">{sub}</div>}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex flex-col items-end">
      <span className="font-semibold text-text-primary">{value.toLocaleString()}</span>
      <span className="text-[10px] uppercase tracking-wide text-text-muted">{label}</span>
    </div>
  );
}

function WebhookPanel({ flowUid }: { flowUid: string }) {
  const [secret, setSecret] = useState<string | null>(null);
  const [revealed, setRevealed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    botFlowsService
      .getWebhook(flowUid)
      .then((r) => { if (!cancelled) setSecret(r.secret); })
      .catch(() => { /* secret stays null; user can still see the URL placeholder */ });
    return () => { cancelled = true; };
  }, [flowUid]);

  const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
  const url = `${baseUrl}/api/v1/slighter_crm/webhooks/bot-flow/${flowUid}/trigger?token=${secret ?? '<token>'}`;
  const sampleBody = JSON.stringify({ phone: '+15551234567', name: 'Jane Doe', customFields: {} }, null, 2);

  const copy = (text: string) => {
    navigator.clipboard.writeText(text).then(
      () => toast.success('Copied to clipboard'),
      () => toast.error('Copy failed'),
    );
  };

  const rotate = async () => {
    if (!window.confirm('Rotate the webhook secret? Any system using the old token will stop working until you update it.')) return;
    setBusy(true);
    try {
      const r = await botFlowsService.rotateWebhook(flowUid);
      setSecret(r.secret);
      setRevealed(true);
      toast.success('New secret generated');
    } catch {
      toast.error('Failed to rotate secret');
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-border bg-surface">
      <div className="flex items-center justify-between border-b border-border px-5 py-3">
        <div>
          <h3 className="font-display text-sm font-semibold text-text-primary">Trigger from outside</h3>
          <p className="mt-0.5 text-xs text-text-muted">POST a phone number from your site, Zapier, or any external system to start this flow.</p>
        </div>
        <button
          onClick={rotate}
          disabled={busy || !secret}
          className="rounded-lg border border-border bg-surface-alt px-2.5 py-1 text-xs font-semibold text-text-secondary transition hover:border-warning/40 hover:text-warning disabled:opacity-50"
          title="Rotate the webhook secret"
        >
          Rotate
        </button>
      </div>
      <div className="px-5 py-4 space-y-3">
        <div>
          <div className="text-2xs font-semibold uppercase tracking-[0.14em] text-text-muted mb-1">Webhook URL</div>
          <div className="flex items-stretch gap-2">
            <code className="flex-1 rounded-lg border border-border bg-surface-alt px-3 py-2 text-xs font-mono text-text-primary break-all">
              {revealed || !secret ? url : url.replace(secret, '••••••••••••••••')}
            </code>
            <button
              onClick={() => setRevealed((v) => !v)}
              className="rounded-lg border border-border bg-surface-alt px-3 text-xs font-semibold text-text-secondary hover:border-primary/30"
            >
              {revealed ? 'Hide' : 'Reveal'}
            </button>
            <button
              onClick={() => copy(url)}
              disabled={!secret}
              className="rounded-lg border border-primary/30 bg-primary-soft px-3 text-xs font-semibold text-primary hover:brightness-110 disabled:opacity-50"
            >
              Copy
            </button>
          </div>
          <p className="mt-1.5 text-2xs text-text-muted">
            Anyone with this URL can start the flow — keep it private. Rotate the secret if it's ever exposed.
          </p>
        </div>
        <div>
          <div className="text-2xs font-semibold uppercase tracking-[0.14em] text-text-muted mb-1">Example body (POST JSON)</div>
          <pre className="rounded-lg border border-border bg-surface-alt p-3 text-2xs font-mono text-text-secondary overflow-x-auto">{sampleBody}</pre>
        </div>
      </div>
    </div>
  );
}

// ─── Simulator tab ────────────────────────────────────────────────────────────

interface SimulatorTabProps {
  flow: BotFlow;
}

function SimulatorTab({ flow }: SimulatorTabProps) {
  const [profileName, setProfileName] = useState('Test User');
  const [phone, setPhone] = useState('+15550000000');
  const [inputText, setInputText] = useState('');
  const [history, setHistory] = useState<SimHistoryEntry[]>([]);
  const [currentStateId, setCurrentStateId] = useState<string | null>(null);
  const [fieldsAfter, setFieldsAfter] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  // Convert history to transcript messages
  const transcriptMessages: TranscriptMessage[] = useMemo(() => {
    return history.map((h, i) => ({
      id: String(i),
      role: h.role,
      body: h.body,
      timestamp: new Date().toISOString(),
      isBot: h.role === 'outbound',
    }));
  }, [history]);

  const reset = () => {
    setHistory([]);
    setCurrentStateId(null);
    setFieldsAfter({});
    setDone(false);
    setInputText('');
  };

  const sendMessage = async () => {
    if (!inputText.trim() || loading) return;
    const inbound = inputText.trim();
    setInputText('');
    setLoading(true);

    const newHistory = [...history, { role: 'inbound' as const, body: inbound }];
    setHistory(newHistory);

    try {
      const result = await botFlowsService.simulate(flow.flowUid, {
        profileName,
        phone,
        history: [
          ...(currentStateId ? [{ role: 'system' as const, body: `state:${currentStateId}` }] : []),
          ...Object.entries(fieldsAfter).map(([k, v]) => ({
            role: 'system' as const,
            body: `field:${k}=${v}`,
          })),
        ],
        inbound,
      });

      const outboundHistory: SimHistoryEntry[] = result.outbound.map((o) => ({
        role: 'outbound',
        body: o.body,
      }));

      setHistory([...newHistory, ...outboundHistory]);
      setCurrentStateId(result.nextState);
      setFieldsAfter(result.fieldsAfter);
      if (result.terminal) setDone(true);
    } catch {
      toast.error('Simulator error');
    } finally {
      setLoading(false);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    }
  };

  // Auto-start on mount
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const result = await botFlowsService.simulate(flow.flowUid, {
          profileName,
          phone,
          history: [],
          inbound: '',
        });
        const outboundHistory: SimHistoryEntry[] = result.outbound.map((o) => ({
          role: 'outbound',
          body: o.body,
        }));
        setHistory(outboundHistory);
        setCurrentStateId(result.nextState);
        setFieldsAfter(result.fieldsAfter);
        if (result.terminal) setDone(true);
      } catch {
        // Flow not yet saved or server error — skip
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex h-full flex-col">
      {/* Lead snapshot */}
      <div className="flex items-center gap-3 border-b border-border px-4 py-3 bg-surface-alt">
        <Bot size={16} className="text-primary" />
        <span className="text-xs font-semibold text-text-muted uppercase tracking-wider">Simulating as</span>
        <Input
          value={profileName}
          onChange={(e) => setProfileName(e.target.value)}
          className="!h-7 !text-xs w-32"
        />
        <Input
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className="!h-7 !text-xs w-36"
        />
        <div className="flex-1" />
        {currentStateId && (
          <span className="text-xs text-text-muted">State: <span className="font-mono text-text-secondary">{currentStateId}</span></span>
        )}
        <Button variant="ghost" size="sm" iconLeft={<RotateCcw size={12} />} onClick={reset}>
          Reset
        </Button>
      </div>

      {/* Transcript */}
      <div className="flex-1 overflow-y-auto bg-bg">
        {history.length === 0 && !loading ? (
          <div className="flex flex-col items-center justify-center gap-2 py-16 text-text-muted">
            <Bot size={32} className="opacity-30" />
            <p className="text-sm">Type a message below to start the simulation</p>
          </div>
        ) : (
          <ConversationTranscript messages={transcriptMessages} loading={loading && history.length === 0} />
        )}
        {done && (
          <div className="flex justify-center py-4">
            <span className="rounded-full bg-success-soft text-success px-4 py-1.5 text-xs font-semibold">
              Flow completed — lead status: {Object.values(fieldsAfter).length > 0 ? JSON.stringify(fieldsAfter) : 'terminal'}
            </span>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Fields collected */}
      {Object.keys(fieldsAfter).length > 0 && (
        <div className="border-t border-border px-4 py-2 bg-surface-alt flex flex-wrap gap-2">
          <span className="text-xs text-text-muted">Collected:</span>
          {Object.entries(fieldsAfter).map(([k, v]) => (
            <span key={k} className="rounded-full bg-primary-soft px-2 py-0.5 text-[10px] font-mono text-primary">
              {k}={v}
            </span>
          ))}
        </div>
      )}

      {/* Input */}
      <div className="flex items-center gap-2 border-t border-border px-4 py-3 bg-surface">
        <input
          className={clsx(
            'flex-1 h-9 rounded-lg border border-border bg-surface-alt px-3 text-sm text-text-primary',
            'focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary-soft',
          )}
          placeholder={done ? 'Flow complete — reset to restart' : 'Type a message...'}
          value={inputText}
          disabled={done || loading}
          onChange={(e) => setInputText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') sendMessage(); }}
        />
        <Button
          size="sm"
          iconLeft={<Send size={13} />}
          onClick={sendMessage}
          disabled={done || loading || !inputText.trim()}
          loading={loading}
        >
          Send
        </Button>
      </div>
    </div>
  );
}

// ─── Context menu ─────────────────────────────────────────────────────────────

interface CtxMenu { x: number; y: number; stateId: string }

// ─── Main Component ───────────────────────────────────────────────────────────

function BotFlowDetailInner() {
  const { flowUid } = useParams<{ flowUid: string }>();
  const navigate = useNavigate();
  const isNew = flowUid === 'new' || !flowUid;

  const [flow, setFlow] = useState<BotFlow | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [isDirty, setIsDirty] = useState(isNew);
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [tab, setTab] = useState<TabId>('editor');
  const [selectedStateId, setSelectedStateId] = useState<string | null>(null);
  const [jsonText, setJsonText] = useState('');
  const [templates, setTemplates] = useState<Template[]>([]);
  const [ctxMenu, setCtxMenu] = useState<CtxMenu | null>(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handler = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // On narrow widths, force JSON tab
  useEffect(() => {
    if (windowWidth < 900 && tab === 'editor') setTab('json');
  }, [windowWidth, tab]);

  // Load templates — scoped to the active WhatsApp account so the picker only
  // surfaces templates approved for THIS WABA; cross-account templates would
  // be rejected by Meta at send time.
  const { active: activeBotFlowAccount, accounts: allBotFlowAccounts } = useActiveAccount();
  useEffect(() => {
    templatesService.list({ limit: 100, accountUid: activeBotFlowAccount?.accountUid })
      .then((r) => setTemplates(r.items)).catch(() => {});
  }, [activeBotFlowAccount?.accountUid]);

  // Load or init flow
  const load = async () => {
    if (isNew) {
      const newFlow: BotFlow = {
        _id: '',
        flowUid: 'new',
        companyUid: '',
        name: 'Untitled flow',
        triggerType: 'ad',
        triggerConfig: {},
        states: [{ id: 'end', type: 'end', terminalStatus: 'qualified' }],
        startStateId: 'end',
        isActive: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      setFlow(newFlow);
      setJsonText(JSON.stringify(newFlow, null, 2));
      return;
    }
    setLoading(true);
    try {
      const data = await botFlowsService.get(flowUid!);
      setFlow(data);
      setJsonText(JSON.stringify(data, null, 2));
    } catch {
      toast.error('Failed to load bot flow');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [flowUid]);

  // Unsaved changes guard
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (isDirty) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const validation = useMemo(() => {
    if (!flow) return null;
    return validateFlowClient(flow.states, flow.startStateId);
  }, [flow]);

  const errorCount = validation?.errors.length ?? 0;

  const updateFlow = useCallback((patch: Partial<BotFlow>) => {
    setFlow((prev) => prev ? { ...prev, ...patch } : prev);
    setIsDirty(true);
  }, []);

  const handleStateChange = useCallback((updated: BotState) => {
    setFlow((prev) => {
      if (!prev) return prev;
      const oldId = updated.id;
      // Check if the id matches the one being edited (look by position in the selected state)
      const idx = prev.states.findIndex((s) => s.id === selectedStateId);
      if (idx === -1) return prev;
      const newStates = [...prev.states];
      const oldStateId = newStates[idx].id;
      newStates[idx] = updated;

      // If ID changed, update all references
      let newStartStateId = prev.startStateId;
      if (oldStateId !== oldId) {
        newStartStateId = prev.startStateId === oldStateId ? oldId : prev.startStateId;
        for (let i = 0; i < newStates.length; i++) {
          if (i === idx) continue;
          newStates[i] = renameStateRef(newStates[i], oldStateId, oldId);
        }
      }

      setSelectedStateId(updated.id);
      return { ...prev, states: newStates, startStateId: newStartStateId };
    });
    setIsDirty(true);
  }, [selectedStateId]);

  const handleDeleteState = useCallback((stateId: string) => {
    setFlow((prev) => {
      if (!prev) return prev;
      const newStates = prev.states.filter((s) => s.id !== stateId);
      return { ...prev, states: newStates };
    });
    setSelectedStateId(null);
    setIsDirty(true);
  }, []);

  const handleAddState = (type: string) => {
    // Strip the virtual ":suffix" used by the sidebar quick-adds so the actual
    // state.type stays one of the schema-known values (always 'choice' here).
    const [baseType, preset] = type.split(':') as [StateTypeKey, string | undefined];
    const baseId = `${baseType}_${Date.now().toString(36)}`;
    let newState: BotState;
    switch (baseType) {
      case 'message':
        newState = { id: baseId, type: 'message', prompt: '' };
        break;
      case 'choice':
        if (preset === 'media_buttons') {
          newState = {
            id: baseId,
            type: 'choice',
            prompt: '',
            presentationType: 'media_buttons',
            mediaType: 'image',
            mediaUrl: '',
            options: [],
          };
        } else if (preset === 'list') {
          newState = {
            id: baseId,
            type: 'choice',
            prompt: '',
            presentationType: 'list',
            buttonText: 'View options',
            sections: [{ title: 'Options', rows: [] }],
            options: [],
          };
        } else {
          newState = { id: baseId, type: 'choice', prompt: '', options: [] };
        }
        break;
      case 'collect':
        newState = { id: baseId, type: 'collect', prompt: '', expectedType: 'text', saveAs: baseId, next: '' };
        break;
      case 'condition':
        newState = { id: baseId, type: 'condition', branches: [], fallbackStateId: '' };
        break;
      case 'handoff':
        newState = { id: baseId, type: 'handoff', assignTo: 'unassigned' };
        break;
      case 'ai':
        newState = { id: baseId, type: 'ai', systemPrompt: '', endIntents: ['interested', 'human_requested'], intentToNextState: {}, maxTurnsBeforeHandoff: 10 };
        break;
      case 'end':
        newState = { id: baseId, type: 'end', terminalStatus: 'qualified' };
        break;
      default:
        return;
    }
    setFlow((prev) => {
      if (!prev) return prev;
      return { ...prev, states: [...prev.states, newState] };
    });
    setSelectedStateId(baseId);
    setIsDirty(true);
  };

  const handleContextMenu = useCallback((e: React.MouseEvent, stateId: string) => {
    e.preventDefault();
    setCtxMenu({ x: e.clientX, y: e.clientY, stateId });
  }, []);

  const handleSetStart = (stateId: string) => {
    updateFlow({ startStateId: stateId });
    setCtxMenu(null);
  };

  const handleSave = async () => {
    if (!flow || errorCount > 0) return;
    // Editing a LIVE flow: leads already mid-flow stay pinned to the snapshot
    // they started on (versioning) — only NEW conversations get this edit.
    // Without this confirm, operators assume in-flight chats update too.
    if (!isNew && flow.isActive) {
      const ok = window.confirm(
        'This flow is live. Customers currently mid-conversation will finish on the version they started with — only new conversations use your changes. Save anyway?',
      );
      if (!ok) return;
    }
    setSaving(true);
    try {
      let saved: BotFlow;
      if (isNew) {
        saved = await botFlowsService.create({
          name: flow.name,
          description: flow.description,
          accountUid: flow.accountUid ?? activeBotFlowAccount?.accountUid,
          triggerType: flow.triggerType,
          triggerConfig: flow.triggerConfig,
          states: flow.states,
          startStateId: flow.startStateId,
          isActive: false,
        });
        toast.success('Flow created');
        navigate(`/leads/bot-flows/${saved.flowUid}`, { replace: true });
      } else {
        saved = await botFlowsService.update(flow.flowUid, {
          name: flow.name,
          description: flow.description,
          accountUid: flow.accountUid,
          triggerType: flow.triggerType,
          triggerConfig: flow.triggerConfig,
          states: flow.states,
          startStateId: flow.startStateId,
        });
        setFlow(saved);
        setJsonText(JSON.stringify(saved, null, 2));
        toast.success('Flow saved');
      }
      setIsDirty(false);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveJson = async () => {
    if (!flow || !jsonText.trim()) return;
    setSaving(true);
    try {
      const parsed = JSON.parse(jsonText) as BotFlow;
      let saved: BotFlow;
      if (isNew) {
        saved = await botFlowsService.create({
          name: parsed.name,
          description: parsed.description,
          accountUid: parsed.accountUid ?? activeBotFlowAccount?.accountUid,
          triggerType: parsed.triggerType,
          triggerConfig: parsed.triggerConfig,
          states: parsed.states,
          startStateId: parsed.startStateId,
          isActive: false,
        });
        toast.success('Flow created');
        navigate(`/leads/bot-flows/${saved.flowUid}`, { replace: true });
      } else {
        saved = await botFlowsService.update(flow.flowUid, {
          name: parsed.name,
          description: parsed.description,
          accountUid: parsed.accountUid,
          triggerType: parsed.triggerType,
          triggerConfig: parsed.triggerConfig,
          states: parsed.states,
          startStateId: parsed.startStateId,
        });
        setFlow(saved);
        setJsonText(JSON.stringify(saved, null, 2));
        toast.success('Flow saved');
      }
      setIsDirty(false);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async () => {
    if (!flow || isNew) return;
    if (!flow.isActive && errorCount > 0) {
      toast.error('Fix validation errors before activating');
      return;
    }
    setToggling(true);
    try {
      const updated = flow.isActive
        ? await botFlowsService.deactivate(flow.flowUid)
        : await botFlowsService.activate(flow.flowUid);
      setFlow(updated);
      toast.success(updated.isActive ? 'Flow activated' : 'Flow deactivated');
    } catch {
      toast.error('Failed to toggle flow');
    } finally {
      setToggling(false);
    }
  };

  const handleDelete = async () => {
    if (!flow || isNew) return;
    if (!window.confirm('Delete this bot flow? This cannot be undone.')) return;
    setDeleting(true);
    try {
      await botFlowsService.remove(flow.flowUid);
      toast.success('Flow deleted');
      navigate('/leads/bot-flows');
    } catch {
      toast.error('Failed to delete flow');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <>
        <WhatsAppHeader section="automation" title="Bot Flow" breadcrumb="Bot Flows" />
        <div className="px-7 py-7 w-full space-y-4">
          <div className="skeleton h-8 w-64 rounded" />
          <div className="skeleton h-96 w-full rounded-2xl" />
        </div>
      </>
    );
  }

  if (!flow) {
    return (
      <>
        <WhatsAppHeader section="automation" title="Not found" breadcrumb="Bot Flows" />
        <div className="px-7 py-7 text-sm text-text-muted">This bot flow does not exist.</div>
      </>
    );
  }

  const selectedState = flow.states.find((s) => s.id === selectedStateId) ?? null;

  const tabs: { id: TabId; label: string }[] = [
    { id: 'editor', label: 'Editor' },
    { id: 'json', label: 'JSON' },
    { id: 'simulator', label: 'Simulator' },
    { id: 'analytics', label: 'Analytics' },
  ];

  const triggerPills: string[] = [];
  const tc = flow.triggerConfig ?? {};
  if (tc.adIds?.length) triggerPills.push(`${tc.adIds.length} ad`);
  if (tc.adSetIds?.length) triggerPills.push(`${tc.adSetIds.length} adset`);
  if (tc.campaignIds?.length) triggerPills.push(`${tc.campaignIds.length} campaign`);
  if (tc.formIds?.length) triggerPills.push(`${tc.formIds.length} form`);
  if (tc.keywords?.length) triggerPills.push(`${tc.keywords.length} keyword`);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <WhatsAppNav section="automation" />
      {/* Topbar */}
      <div className="flex items-center gap-3 border-b border-border px-6 py-3 bg-surface shrink-0">
        {/* Editable title */}
        <input
          className="font-display text-base font-semibold text-text-primary bg-transparent border-b border-transparent hover:border-border focus:border-primary focus:outline-none transition min-w-[120px] max-w-xs"
          value={flow.name}
          onChange={(e) => updateFlow({ name: e.target.value })}
        />

        {/* WhatsApp account binding — this flow only fires for inbound messages on this account */}
        {(() => {
          const boundAccount = flow.accountUid
            ? allBotFlowAccounts.find((a) => a.accountUid === flow.accountUid)
            : null;
          if (boundAccount) {
            return (
              <div
                className="hidden md:inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary-soft/50 px-2.5 py-0.5 text-[11px] font-medium text-primary"
                title="This flow only runs for inbound messages received on this WhatsApp account"
              >
                <Phone size={11} />
                {boundAccount.displayPhoneNumber || boundAccount.wabaId || 'WhatsApp account'}
              </div>
            );
          }
          // Legacy flow with no binding — let the user bind it to the active account
          if (activeBotFlowAccount) {
            return (
              <button
                type="button"
                onClick={() => updateFlow({ accountUid: activeBotFlowAccount.accountUid })}
                className="hidden md:inline-flex items-center gap-1.5 rounded-full border border-dashed border-text-muted/40 px-2.5 py-0.5 text-[11px] font-medium text-text-muted hover:border-primary/40 hover:text-primary transition-colors"
                title={`Bind this flow to ${activeBotFlowAccount.displayPhoneNumber || 'the active account'} — it will then only fire on that account`}
              >
                <Phone size={11} />
                Any account · bind to {activeBotFlowAccount.displayPhoneNumber || 'active'}
              </button>
            );
          }
          return null;
        })()}

        {/* Trigger pills */}
        {triggerPills.length > 0 && (
          <div className="flex items-center gap-1">
            <span className="text-xs text-text-muted">Trigger:</span>
            <span className="rounded-full bg-primary-soft text-primary px-2 py-0.5 text-[10px] font-semibold">
              {TRIGGER_LABELS[flow.triggerType]}
            </span>
            {triggerPills.map((p) => (
              <span key={p} className="rounded-full bg-surface-alt border border-border text-text-muted px-2 py-0.5 text-[10px]">
                {p}
              </span>
            ))}
          </div>
        )}

        <div className="flex-1" />

        {/* Right actions */}
        <div className="flex items-center gap-2">
          {!isNew && (
            <button
              onClick={handleToggleActive}
              disabled={toggling || (!flow.isActive && errorCount > 0)}
              title={!flow.isActive && errorCount > 0 ? 'Fix validation errors before activating' : undefined}
              className={clsx(
                'flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium transition',
                flow.isActive
                  ? 'bg-green-50 text-green-700 hover:bg-green-100 dark:bg-green-900/20 dark:text-green-400'
                  : 'bg-surface-alt text-text-muted hover:bg-border',
                (toggling || (!flow.isActive && errorCount > 0)) && 'opacity-50 cursor-not-allowed',
              )}
            >
              {flow.isActive
                ? <><CheckCircle size={12} /> Active</>
                : <><Circle size={12} /> Inactive</>
              }
            </button>
          )}

          <Button
            variant="primary"
            size="sm"
            iconLeft={<Save size={13} />}
            loading={saving}
            disabled={(!isDirty && !isNew) || (tab !== 'json' && errorCount > 0)}
            onClick={tab === 'json' ? handleSaveJson : handleSave}
            title={errorCount > 0 && tab !== 'json' ? `${errorCount} validation error(s) — fix before saving` : undefined}
          >
            Save
            {errorCount > 0 && tab !== 'json' && (
              <span className="ml-1 rounded-full bg-danger/20 text-danger px-1.5 py-0.5 text-[10px] font-bold">
                {errorCount}
              </span>
            )}
          </Button>

          {!isNew && (
            <Button
              variant="ghost"
              size="sm"
              iconLeft={<Trash2 size={13} />}
              loading={deleting}
              onClick={handleDelete}
            >
              Delete
            </Button>
          )}
        </div>
      </div>

      {/* Narrow-screen warning */}
      {windowWidth < 900 && (
        <div className="bg-warning-soft border-b border-warning/20 px-6 py-2 text-xs text-warning flex items-center gap-2">
          <AlertTriangle size={13} />
          Visual editing requires a wider screen (min 900px). Showing JSON editor.
        </div>
      )}

      {/* Tab bar */}
      <div className="flex gap-4 border-b border-border px-6 bg-surface shrink-0">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            disabled={windowWidth < 900 && t.id === 'editor'}
            className={clsx(
              'pb-3 pt-3 text-sm font-medium border-b-2 transition',
              tab === t.id
                ? 'border-primary text-primary'
                : 'border-transparent text-text-muted hover:text-text-primary',
              windowWidth < 900 && t.id === 'editor' && 'opacity-40 cursor-not-allowed',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-hidden">
        {/* ─── Editor tab ─── */}
        {tab === 'editor' && (
          <div className="flex h-full">
            {/* Node palette */}
            <div className="w-44 shrink-0 border-r border-border bg-surface overflow-y-auto">
              <div className="p-3 space-y-1">
                <p className="px-1 py-1 text-[10px] font-semibold text-text-muted uppercase tracking-wider">
                  Add node
                </p>
                {STATE_TYPES.map(({ type, icon, label, desc }) => (
                  <button
                    key={type}
                    onClick={() => handleAddState(type)}
                    className={clsx(
                      'w-full flex items-start gap-2.5 rounded-lg p-2 text-left transition',
                      'hover:bg-surface-alt active:scale-95',
                    )}
                  >
                    <span className="mt-0.5 shrink-0 text-text-muted">{icon}</span>
                    <div className="min-w-0">
                      <p className="text-[11px] font-semibold text-text-primary">{label}</p>
                      <p className="text-[10px] text-text-muted leading-tight line-clamp-2">{desc}</p>
                    </div>
                  </button>
                ))}
              </div>

              {/* Validation summary */}
              {validation && (validation.errors.length > 0 || validation.warnings.length > 0) && (
                <div className="mx-3 mb-3 rounded-lg border border-border p-2 space-y-1">
                  {validation.errors.length > 0 && (
                    <p className="text-[10px] font-semibold text-danger flex items-center gap-1">
                      <AlertTriangle size={9} /> {validation.errors.length} error{validation.errors.length > 1 ? 's' : ''}
                    </p>
                  )}
                  {validation.warnings.length > 0 && (
                    <p className="text-[10px] font-semibold text-warning flex items-center gap-1">
                      <Info size={9} /> {validation.warnings.length} warning{validation.warnings.length > 1 ? 's' : ''}
                    </p>
                  )}
                  {validation.errors.slice(0, 3).map((e, i) => (
                    <p key={i} className="text-[9px] text-danger leading-tight">{e.message}</p>
                  ))}
                </div>
              )}
            </div>

            {/* Canvas */}
            <div className="flex-1 min-w-0 relative overflow-hidden">
              <ReactFlowProvider>
                <CanvasInner
                  flow={flow}
                  selectedStateId={selectedStateId}
                  onSelectState={setSelectedStateId}
                  onFlowChange={(updated) => { setFlow(updated); setIsDirty(true); }}
                  onContextMenu={handleContextMenu}
                  errorsByStateId={validation?.errorsByStateId ?? new Map()}
                  warningsByStateId={validation?.warningsByStateId ?? new Map()}
                  templates={templates}
                />
              </ReactFlowProvider>
            </div>

            {/* Right panel */}
            <div className="w-80 shrink-0 border-l border-border bg-surface overflow-hidden flex flex-col">
              {selectedState ? (
                <StatePropPanel
                  state={selectedState}
                  allStates={flow.states}
                  templates={templates}
                  onChange={handleStateChange}
                  onDelete={handleDeleteState}
                />
              ) : (
                <TriggerPanel flow={flow} onChange={updateFlow} />
              )}
            </div>
          </div>
        )}

        {/* ─── JSON tab ─── */}
        {tab === 'json' && (
          <div className="p-6 h-full overflow-y-auto">
            <Card padded className="max-w-3xl">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-semibold text-text-primary">Raw JSON (editable)</p>
                <Button
                  size="sm"
                  iconLeft={<Save size={13} />}
                  loading={saving}
                  onClick={handleSaveJson}
                >
                  Save
                </Button>
              </div>
              <p className="text-xs text-text-muted mb-2">
                Edit the flow JSON directly and click Save. The API validates state IDs and transitions.
              </p>
              <textarea
                value={jsonText}
                onChange={(e) => { setJsonText(e.target.value); setIsDirty(true); }}
                rows={32}
                className={clsx(
                  'w-full resize-y rounded-xl border border-border bg-surface-alt px-3 py-2.5',
                  'font-mono text-xs text-text-primary placeholder:text-text-muted',
                  'focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary-soft transition',
                )}
                spellCheck={false}
              />
            </Card>
          </div>
        )}

        {/* ─── Simulator tab ─── */}
        {tab === 'simulator' && (
          <div className="h-full">
            {isNew || !flow.flowUid || flow.flowUid === 'new' ? (
              <div className="flex flex-col items-center justify-center gap-3 py-24 text-center text-text-muted">
                <Bot size={32} className="opacity-30" />
                <p className="text-sm">Save the flow first to use the simulator.</p>
              </div>
            ) : (
              <SimulatorTab flow={flow} />
            )}
          </div>
        )}

        {/* ─── Analytics tab ─── */}
        {tab === 'analytics' && (
          <div className="h-full overflow-y-auto">
            {isNew || !flow.flowUid || flow.flowUid === 'new' ? (
              <div className="flex flex-col items-center justify-center gap-3 py-24 text-center text-text-muted">
                <Bot size={32} className="opacity-30" />
                <p className="text-sm">Save and activate the flow to start collecting analytics.</p>
              </div>
            ) : (
              <AnalyticsTab flow={flow} />
            )}
          </div>
        )}
      </div>

      {/* Context menu */}
      {ctxMenu && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setCtxMenu(null)} />
          <div
            className="fixed z-50 rounded-xl border border-border bg-surface shadow-elevated p-1 min-w-[160px]"
            style={{ left: ctxMenu.x, top: ctxMenu.y }}
          >
            <button
              className="w-full flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-text-primary hover:bg-surface-alt transition"
              onClick={() => handleSetStart(ctxMenu.stateId)}
            >
              <CheckCircle size={13} className="text-success" />
              Set as START
            </button>
            <button
              className="w-full flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-text-primary hover:bg-surface-alt transition"
              onClick={() => { setSelectedStateId(ctxMenu.stateId); setCtxMenu(null); }}
            >
              <PenLine size={13} className="text-text-muted" />
              Edit state
            </button>
            <button
              className="w-full flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-danger hover:bg-danger-soft/50 transition"
              onClick={() => { handleDeleteState(ctxMenu.stateId); setCtxMenu(null); }}
            >
              <Trash2 size={13} />
              Delete state
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Rename state references ──────────────────────────────────────────────────

function renameStateRef(state: BotState, oldId: string, newId: string): BotState {
  const s = { ...state } as Record<string, unknown>;
  if (s.next === oldId) s.next = newId;
  if (s.fallbackStateId === oldId) s.fallbackStateId = newId;
  if (Array.isArray(s.options)) {
    s.options = (s.options as ChoiceOption[]).map((o) => ({
      ...o,
      nextStateId: o.nextStateId === oldId ? newId : o.nextStateId,
    }));
  }
  if (Array.isArray(s.branches)) {
    s.branches = (s.branches as ConditionBranch[]).map((b) => ({
      ...b,
      nextStateId: b.nextStateId === oldId ? newId : b.nextStateId,
    }));
  }
  return s as unknown as BotState;
}

// ─── Export ───────────────────────────────────────────────────────────────────

export default function BotFlowDetail() {
  return (
    <ReactFlowProvider>
      <BotFlowDetailInner />
    </ReactFlowProvider>
  );
}
